import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import TopNav from '../components/feature/TopNav';
import { parseCaseRouteParam } from '../services/advisorApiAdapter';
import { mvpApi } from '../services/api';
import ActivityInputBar from '../components/feature/ActivityInputBar';
import DocumentsTab from './case-detail/components/DocumentsTab';
import CollapsibleSection from './case-detail/components/CollapsibleSection';
import {
  categoryIsComplete,
  categorySidebarIconClass,
  categorySidebarStatus,
  fileIconClassName,
  mergeRequiredWithUploads,
} from './case-detail/requiredDocuments';
import { formatCaseWorkflowStatus } from '../utils/caseWorkflowStatus';
import { formatCaseHistoryAt } from '../utils/caseHistoryClient';
import { formatDisplayDateTime } from '../utils/formatDisplayDateTime';
import { birthDateToHtmlInputValue, maxBirthDateIsoForAdult } from '../utils/advisorDateInput';
import { caseHistoryKindVisual } from '../utils/caseHistoryVisual';
import CaseAdvisorPicker from '../components/advisor/CaseAdvisorPicker';
import MobileFieldWithVerify, { extractLocalNumber } from '../components/feature/MobileFieldWithVerify';
import { ALL_DIAL_CODES } from '../constants/phoneDialCodes';
import GenerateFormModal from './case-detail/components/GenerateFormModal';
import DownloadPackageModal from './case-detail/components/DownloadPackageModal';
import CaseTypeEditor from './case-detail/components/CaseTypeEditor';
import ServiceFeeCard from './case-detail/components/ServiceFeeCard';
import { APPLICANT_SEX_OPTIONS, canonicalApplicantSex } from '../utils/applicantSexOptions';
import { showPopupMessage } from '../utils/showPopup';
import { readAdvisorCasesListReturn } from '../utils/casesListUrl';
import { SkeletonCaseDetail, SkeletonCaseDetailContent } from '../components/advisor/SkeletonLoader';
import {
  DEFAULT_NOTIFICATION_COLLECTED,
  DEFAULT_REPRESENTATIVE_COLLECTED,
  displayCollectedLine,
  effectiveStringForEdit,
  formatElectronicNotificationsDisplay,
} from '../constants/representativeNotificationDefaults';
import { SPAIN_ADDRESS_FLOOR_OPTIONS, SPAIN_PROVINCE_OPTIONS } from '../constants/spainAddressOptions';
import {
  isValidAdultDobRule,
  isValidCityRule,
  isValidEmailRule,
  isValidName40,
  isValidName80,
  isValidNieRule,
  isValidPassportRule,
  isValidPhoneRule,
  isValidPostalCodeRule,
  isValidStreetNumberRule,
  isValidStreetRule,
} from '../utils/advisorFieldValidation';
import { capitalizeWords, isStrictEmail } from '../utils/formInputUtils';

function initialCaseResolve(caseIdParam) {
  const spec = parseCaseRouteParam(caseIdParam);
  if (spec.mode === 'legacy') return { status: 'ready', userId: spec.userId, sessionId: spec.sessionId };
  if (spec.mode === 'public') return { status: 'resolving', userId: '', sessionId: '' };
  return { status: 'error', userId: '', sessionId: '' };
}

function detectDialCode(phoneRaw, fallback = '+34') {
  const normalized = String(phoneRaw || '').trim().replace(/\s+/g, '');
  if (!normalized.startsWith('+')) return fallback;
  const sorted = [...ALL_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  const match = sorted.find((d) => normalized.startsWith(d.code));
  return match?.code || fallback;
}

const HELP_TIMELINE_KINDS = new Set([
  'applicant_help_message',
  'help_message_replied',
  'family_application_interest_replied',
]);
const FAMILY_APPLICATION_INTEREST_KIND = 'applicant_family_application_interest';

function isNotesTimelineEntry(event) {
  if (!event) return false;
  if (event.category === 'note') return true;
  const kind = typeof event.kind === 'string' ? event.kind : '';
  return HELP_TIMELINE_KINDS.has(kind);
}

function buildHelpRepliedOriginalIdSet(events) {
  return new Set(
    (Array.isArray(events) ? events : [])
      .filter((event) => event?.kind === 'help_message_replied')
      .map((event) => String(event?.meta?.original_help_message_id || '').trim())
      .filter(Boolean),
  );
}

function isPendingApplicantHelpMessage(event, repliedOriginalIds) {
  if (event?.kind !== 'applicant_help_message') return false;
  const isMarkedReplied = event?.meta?.replied === true;
  const eventId = String(event?.id || '').trim();
  const hasLinkedReply = eventId ? repliedOriginalIds.has(eventId) : false;
  return !isMarkedReplied && !hasLinkedReply;
}

function buildFamilyRepliedOriginalIdSet(events) {
  return new Set(
    (Array.isArray(events) ? events : [])
      .filter((event) => event?.kind === 'family_application_interest_replied')
      .map((event) => String(event?.meta?.original_family_interest_id || '').trim())
      .filter(Boolean),
  );
}

function isPendingFamilyApplicationInterest(event, familyRepliedOriginalIds) {
  if (event?.kind !== FAMILY_APPLICATION_INTEREST_KIND) return false;
  if (event?.meta?.replied === true || event?.meta?.dismissed === true) return false;
  const eventId = String(event?.id || '').trim();
  const hasLinkedReply = eventId ? familyRepliedOriginalIds.has(eventId) : false;
  return !hasLinkedReply;
}

function pendingCustomerMessageAt(event) {
  const t = event?.at ? new Date(event.at).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
}

function collectPendingCustomerMessageEvents(events) {
  if (!Array.isArray(events) || events.length === 0) return [];
  const repliedOriginalIds = buildHelpRepliedOriginalIdSet(events);
  const familyRepliedOriginalIds = buildFamilyRepliedOriginalIdSet(events);
  return events.filter(
    (event) =>
      isPendingApplicantHelpMessage(event, repliedOriginalIds) ||
      isPendingFamilyApplicationInterest(event, familyRepliedOriginalIds),
  );
}

function hasPendingHelpMessages(events) {
  return collectPendingCustomerMessageEvents(events).length > 0;
}

function getFirstPendingHelpMessage(events) {
  const pending = collectPendingCustomerMessageEvents(events);
  if (!pending.length) return null;
  const sorted = [...pending].sort((a, b) => pendingCustomerMessageAt(b) - pendingCustomerMessageAt(a));
  return sorted[0];
}

export default function AdvisorCaseDetail() {
  const { t, i18n } = useTranslation();
  const { caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const casesListReturnTo = useMemo(
    () => location.state?.returnTo || readAdvisorCasesListReturn(),
    [location.state?.returnTo],
  );
  const goBackToCasesList = useCallback(() => {
    navigate(casesListReturnTo);
  }, [navigate, casesListReturnTo]);
  const [caseResolve, setCaseResolve] = useState(() => {
    const s = location.state;
    if (s?.userId && s?.sessionId) {
      return { status: 'ready', userId: s.userId, sessionId: s.sessionId };
    }
    return initialCaseResolve(caseId);
  });

  useEffect(() => {
    if (caseResolve.status === 'ready') return;
    const spec = parseCaseRouteParam(caseId);
    if (spec.mode === 'legacy') {
      setCaseResolve({ status: 'ready', userId: spec.userId, sessionId: spec.sessionId });
      return;
    }
    if (spec.mode === 'public') {
      setCaseResolve({ status: 'resolving', userId: '', sessionId: '' });
      let cancelled = false;
      (async () => {
        try {
          const res = await mvpApi.getCaseByPublicId(spec.publicCaseId);
          if (cancelled) return;
          const d = res?.data;
          if (d?.user_id != null && d?.session_id) {
            setCaseResolve({ status: 'ready', userId: String(d.user_id), sessionId: d.session_id });
          } else {
            setCaseResolve({ status: 'error', userId: '', sessionId: '' });
          }
        } catch {
          if (!cancelled) setCaseResolve({ status: 'error', userId: '', sessionId: '' });
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    setCaseResolve({ status: 'error', userId: '', sessionId: '' });
    return undefined;
  }, [caseId]);

  const userId = caseResolve.status === 'ready' ? caseResolve.userId : '';
  const sessionId = caseResolve.status === 'ready' ? caseResolve.sessionId : '';
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [caseDetail, setCaseDetail] = useState(null);
  const [advisorName, setAdvisorName] = useState(() => location.state?.advisorName || t('advisorPortal.advisorNameFallback'));
  /** History tab list (respects All / Notes / Activity filter). */
  const [history, setHistory] = useState([]);
  /** Full timeline for Details sidebar "Recent history" (always `all` tab; not overwritten by tab filter). */
  const [historyPreviewFull, setHistoryPreviewFull] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [notePosting, setNotePosting] = useState(false);
  const [toast, setToast] = useState('');
  const [helpMessageModal, setHelpMessageModal] = useState(null);
  const [helpReplyText, setHelpReplyText] = useState('');
  const [helpResponding, setHelpResponding] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [savingSection, setSavingSection] = useState(false);
  const [editDraft, setEditDraft] = useState({});
  const [initialEditDraft, setInitialEditDraft] = useState({});
  const [editedFields, setEditedFields] = useState(() => new Set());
  const [fieldErrors, setFieldErrors] = useState({});
  const [highlightDocId, setHighlightDocId] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [generateFormOpen, setGenerateFormOpen] = useState(false);
  const [downloadPackageOpen, setDownloadPackageOpen] = useState(false);
  const [showCaseTypeEditor, setShowCaseTypeEditor] = useState(false);
  const [caseTypeSaving, setCaseTypeSaving] = useState(false);
  const [referralEditing, setReferralEditing] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [referralLookup, setReferralLookup] = useState(null);
  const [referralSaving, setReferralSaving] = useState(false);
  const [contactMobileCountryCode, setContactMobileCountryCode] = useState('+34');
  const [contactMobileChannel, setContactMobileChannel] = useState('SMS');
  const [contactMobileVerified, setContactMobileVerified] = useState(false);
  const [contactMobileVerifying, setContactMobileVerifying] = useState(false);
  const [contactEmailVerified, setContactEmailVerified] = useState(false);
  const [contactEmailVerifying, setContactEmailVerifying] = useState(false);
  const [contactEmailCode, setContactEmailCode] = useState('');
  const [identityViewDocType, setIdentityViewDocType] = useState('');
  const [showCustomerMessageBanner, setShowCustomerMessageBanner] = useState(true);
  const [confirmHideCustomerBannerOpen, setConfirmHideCustomerBannerOpen] = useState(false);
  const [confirmHideCustomerBannerSaving, setConfirmHideCustomerBannerSaving] = useState(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    setShowCustomerMessageBanner(true);
    setConfirmHideCustomerBannerOpen(false);
  }, [caseId]);

  useEffect(() => {
    const shouldHide = Boolean(caseDetail?.case?.advisor_hide_customer_message_banner);
    setShowCustomerMessageBanner(!shouldHide);
  }, [caseDetail?.case?.advisor_hide_customer_message_banner]);

  const refreshCase = useCallback(async () => {
    const detailRes = await mvpApi.getUserCaseDetail(userId, sessionId);
    setCaseDetail(detailRes?.data || null);
  }, [userId, sessionId]);

  const refreshHistoryPreview = useCallback(async () => {
    if (!userId || !sessionId) return;
    try {
      const res = await mvpApi.getCaseHistory(userId, sessionId, { params: { history_tab: 'all' } });
      setHistoryPreviewFull(res?.data?.events || []);
    } catch {
      setHistoryPreviewFull([]);
    }
  }, [userId, sessionId]);

  const loadHistoryTab = useCallback(
    async (tab = 'all') => {
      if (!userId || !sessionId) return;
      setHistoryLoading(true);
      try {
        const requestTab = tab === 'notes' ? 'all' : tab;
        const res = await mvpApi.getCaseHistory(userId, sessionId, { params: { history_tab: requestTab } });
        const allEvents = res?.data?.events || [];
        const events = tab === 'notes' ? allEvents.filter(isNotesTimelineEntry) : allEvents;
        setHistory(events);
        if (tab === 'all') {
          setHistoryPreviewFull(events);
        }
      } catch {
        setHistory([]);
        if (tab === 'all') {
          setHistoryPreviewFull([]);
        }
      } finally {
        setHistoryLoading(false);
      }
    },
    [userId, sessionId],
  );

  const handlePostHistoryNote = useCallback(
    async (entry) => {
      setNotePosting(true);
      try {
        const normalizedKind = entry?.entryInputType || entry?.type || 'internal_note';
        const normalizedText = entry?.text || entry?.detail || '';
        await mvpApi.postCaseHistoryNote(userId, sessionId, {
          kind: normalizedKind,
          text: normalizedText,
          document_tag: entry.documentTag || undefined,
        });
        setToast(t('advisorPortal.postedToHistory'));
        await loadHistoryTab(historyFilter);
        if (historyFilter !== 'all') {
          await refreshHistoryPreview();
        }
      } catch (err) {
        const raw = err?.response?.data?.detail;
        setToast(typeof raw === 'string' ? raw : t('advisorPortal.couldNotPostNote'));
        throw err;
      } finally {
        setNotePosting(false);
      }
    },
    [userId, sessionId, historyFilter, loadHistoryTab, refreshHistoryPreview, t],
  );

  const handleMarkHelpResponded = useCallback(async (messageId) => {
    if (helpResponding) return;
    setHelpResponding(true);
    try {
      const reply = helpReplyText.trim() || null;
      const kind = helpMessageModal?.kind;
      if (kind === 'applicant_family_application_interest') {
        await mvpApi.markFamilyInterestResponded(userId, sessionId, messageId, reply);
      } else {
        await mvpApi.markHelpMessageResponded(userId, sessionId, messageId, reply);
      }
      setHelpMessageModal(null);
      setHelpReplyText('');
      setToast(reply ? t('advisorPortal.helpReplySent') : t('advisorPortal.helpMarkedResponded'));
      await loadHistoryTab(historyFilter);
      if (historyFilter !== 'all') {
        await refreshHistoryPreview();
      }
      await refreshCase();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setToast(typeof raw === 'string' ? raw : t('advisorPortal.couldNotPostNote'));
    } finally {
      setHelpResponding(false);
    }
  }, [
    helpMessageModal?.kind,
    helpResponding,
    helpReplyText,
    userId,
    sessionId,
    historyFilter,
    loadHistoryTab,
    refreshHistoryPreview,
    refreshCase,
    t,
  ]);

  const afterCaseExtensionSave = useCallback(async () => {
    await refreshCase();
    await refreshHistoryPreview();
    await loadHistoryTab(historyFilter);
  }, [refreshCase, refreshHistoryPreview, loadHistoryTab, historyFilter]);

  useEffect(() => {
    if (caseResolve.status !== 'ready' || !caseResolve.userId || !caseResolve.sessionId) {
      if (caseResolve.status === 'error') setLoading(false);
      return;
    }
    // Only run if we haven't loaded yet or if the ID actually changed
    if (!isInitialMount.current && loading === false) return;
    
    let mounted = true;
    setLoading(true);
    async function fetchData() {
      try {
        const [detailRes, meRes] = await Promise.all([
          mvpApi.getUserCaseDetail(caseResolve.userId, caseResolve.sessionId),
          location.state?.advisorName ? Promise.resolve(null) : mvpApi.getMe(),
        ]);
        if (!mounted) return;
        setCaseDetail(detailRes?.data || null);
        if (meRes) {
          const me = meRes?.data?.user || meRes?.data || {};
          const name =
            [me.first_name, me.last_name].filter(Boolean).join(' ').trim() ||
            me.email ||
            i18n.t('advisorPortal.advisorNameFallback');
          setAdvisorName(name);
        } else if (location.state?.advisorName) {
          setAdvisorName(location.state.advisorName);
        }
        if (mounted) await loadHistoryTab('all');
      } finally {
        if (mounted) {
          setLoading(false);
          isInitialMount.current = false;
        }
      }
    }
    fetchData();
    return () => {
      mounted = false;
    };
  }, [caseResolve.status, caseResolve.userId, caseResolve.sessionId, loadHistoryTab]);

  useEffect(() => {
    const pub = caseDetail?.case?.public_case_id;
    if (!pub || caseResolve.status !== 'ready') return;
    const spec = parseCaseRouteParam(caseId);
    if (spec.mode === 'legacy') {
      navigate(`/case/${pub}`, { replace: true });
    }
  }, [caseDetail, caseId, caseResolve.status, navigate]);

  const c = caseDetail?.case || {};
  const displayCaseTypeName = useCallback(
    (value) => {
      const raw = String(value || '').trim();
      if (!raw) return t('advisorPortal.defaultCaseType');
      const lower = raw.toLowerCase();
      if (lower === 'regularization 2026' || lower === 'extraordinary regularization 2026') {
        return t('advisorPortal.defaultCaseType');
      }
      return raw;
    },
    [t],
  );
  const displayedReferralCode = c.case_referral_code || c.applicant_registered_with_code || null;
  const displayedReferralName =
    c.case_referral_ref_name || c.applicant_registered_with_name || null;
  const collected = c.collected_fields || {};
  const documents = c.uploaded_documents || [];
  const archivedDocuments = c.archived_documents || [];

  const mergedDocSlots = useMemo(
    () => mergeRequiredWithUploads(documents, c.prior_asylum_application),
    [documents, c.prior_asylum_application],
  );
  /** Sections with at least one file (upload progress). */
  const categoriesWithUploads = useMemo(
    () => mergedDocSlots.filter((d) => d.uploaded).length,
    [mergedDocSlots],
  );
  /** Sections where every file in the category is accepted (strict completion). */
  const completedDocCategories = useMemo(
    () => mergedDocSlots.filter((d) => categoryIsComplete(d)).length,
    [mergedDocSlots],
  );
  const totalDocCategories = mergedDocSlots.length;

  const read = (...keys) => {
    for (const k of keys) {
      const collectedValue = collected[k];
      if (collectedValue !== undefined && collectedValue !== null && String(collectedValue).trim() !== '') {
        return String(collectedValue);
      }
      const caseValue = c?.[k];
      if (caseValue !== undefined && caseValue !== null && String(caseValue).trim() !== '') {
        return String(caseValue);
      }
    }
    return '—';
  };
  const channelLabel = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    if (s === 'telefono' || s === 'mobile' || s === 'phone') return t('advisorPortal.fieldMobile');
    if (s === 'correo' || s === 'email') return t('advisorPortal.fieldEmail');
    if (s === 'whatsapp') return 'SMS';
    if (s === 'sms') return 'SMS';
    return raw || '—';
  };
  const isTruthyFlag = (raw) => {
    if (raw === true || raw === 1) return true;
    const s = String(raw || '').trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'si' || s === 'sí';
  };
  const languageLabel = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    if (s === 'es' || s === 'spanish' || s === 'espanol' || s === 'español') return t('advisorPortal.newCustomerLangEs');
    if (s === 'en' || s === 'english') return t('advisorPortal.newCustomerLangEn');
    if (s === 'fr' || s === 'french') return t('advisorPortal.newCustomerLangFr');
    if (s === 'ar' || s === 'arabic') return t('advisorPortal.newCustomerLangAr');
    return raw || '—';
  };
  const sexLabel = (raw) => {
    const v = canonicalApplicantSex(raw);
    if (v === 'male') return t('advisorPortal.sexMale');
    if (v === 'female') return t('advisorPortal.sexFemale');
    if (v === 'undetermined') return t('advisorPortal.sexUndetermined');
    return raw || '—';
  };
  const formatDateDmy = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s || s === '—') return '—';
    // Prefer date-only strings (YYYY-MM-DD) without timezone shifting.
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-');
      return `${d}/${m}/${y}`;
    }
    // Fallback: ISO datetime string -> use first 10 chars if it looks like YYYY-MM-DD...
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const [y, m, d] = s.slice(0, 10).split('-');
      if (y && m && d) return `${d}/${m}/${y}`;
    }
    return s;
  };
  const boolLabel = (raw) => {
    if (raw === true) return t('advisorPortal.yes');
    if (raw === false) return t('advisorPortal.no');
    const s = String(raw || '').trim().toLowerCase();
    if (['yes', 'true', 'si', 'sí', '1'].includes(s)) return t('advisorPortal.yes');
    if (['no', 'false', '0'].includes(s)) return t('advisorPortal.no');
    return '—';
  };
  const boolLabelNoIfNull = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return t('advisorPortal.no');
    const normalized = s.toLowerCase();
    if (['null', 'undefined'].includes(normalized)) return t('advisorPortal.no');
    const rendered = boolLabel(raw);
    return rendered === '—' ? t('advisorPortal.no') : rendered;
  };
  const applyingForLabel = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    if (!s) return '—';
    if (['work', '7a'].includes(s)) return t('advisorPortal.pathwayWork');
    if (['family', '7b'].includes(s)) return t('advisorPortal.pathwayFamily');
    if (['vulnerability', '7c'].includes(s)) return t('advisorPortal.pathwayVulnerability');
    return String(raw);
  };
  const normalizeCivilStatusValue = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    const allowed = new Set(['single', 'married', 'widowed', 'divorced', 'separated']);
    return allowed.has(s) ? s : '';
  };
  const civilStatusDisplay = (raw) => {
    const s = String(raw || '').trim();
    if (!s || s === '—' || s === 'â€”') return '';
    const key = s.toLowerCase();
    const labels = {
      single: t('advisorPortal.civilStatusSingle', { defaultValue: 'Single' }),
      married: t('advisorPortal.civilStatusMarried', { defaultValue: 'Married' }),
      widowed: t('advisorPortal.civilStatusWidowed', { defaultValue: 'Widowed' }),
      divorced: t('advisorPortal.civilStatusDivorced', { defaultValue: 'Divorced' }),
      separated: t('advisorPortal.civilStatusSeparated', { defaultValue: 'Separated' }),
    };
    return labels[key] || s;
  };
  const identityDocTypeDisplay = useCallback((raw) => {
    const v = String(raw || '').trim();
    if (!v) return 'â€”';
    const normalized = v.toLowerCase();
    if (normalized === 'passport') return t('section6.docTypeOptions.passport', { defaultValue: 'Passport' });
    if (['national_id', 'nationalid', 'national_id_card', 'national_id_card_id'].includes(normalized)) {
      return t('section6.docTypeOptions.nationalId', { defaultValue: 'National ID card' });
    }
    if (['travel_document', 'traveldoc', 'travel_document_id'].includes(normalized)) {
      return t('section6.docTypeOptions.travelDoc', { defaultValue: 'Travel document' });
    }
    if (['cedula_inscripcion', 'cedulainscripcion', 'registration', 'registration_document', 'cedula_de_inscripcion_id'].includes(normalized)) {
      return t('section6.docTypeOptions.cedulaInscripcion', { defaultValue: 'Registration certificate' });
    }
    if (['other', 'other_id'].includes(normalized)) {
      return t('section6.docTypeOptions.other', { defaultValue: 'Other' });
    }
    return v;
  }, [t]);
  const selectedIdentityDocumentNumber = useCallback(() => {
    const rawType = String(identityViewDocType || read('tipo_documento_identidad', 'identity_doc_type') || '').trim().toLowerCase();
    if (rawType === 'passport') return read('passport_number', 'numero_pasaporte', 'identity_doc_number');
    if (['national_id', 'nationalid', 'national_id_card', 'national_id_card_id'].includes(rawType)) {
      return read('national_id_document_number', 'numero_documento_identidad', 'identity_document_number');
    }
    if (['travel_document', 'traveldoc', 'travel_document_id'].includes(rawType)) return read('travel_document_number');
    if (['cedula_inscripcion', 'cedulainscripcion', 'registration', 'registration_document', 'cedula_de_inscripcion_id'].includes(rawType)) {
      return read('registration_document_number');
    }
    if (['other', 'other_id'].includes(rawType)) return read('other_document_number');
    return read('passport_number', 'numero_pasaporte', 'identity_doc_number');
  }, [identityViewDocType, read]);
  const identityViewDocTypeOptions = useMemo(
    () => [
      { value: 'passport', label: t('section6.docTypeOptions.passport', { defaultValue: 'Passport' }) },
      { value: 'national_id', label: t('section6.docTypeOptions.nationalId', { defaultValue: 'National ID card' }) },
      { value: 'travel_document', label: t('section6.docTypeOptions.travelDoc', { defaultValue: 'Travel document' }) },
      { value: 'cedula_inscripcion', label: t('section6.docTypeOptions.cedulaInscripcion', { defaultValue: 'Registration certificate' }) },
      { value: 'other', label: t('section6.docTypeOptions.other', { defaultValue: 'Other' }) },
    ],
    [t],
  );
  useEffect(() => {
    const saved = String(read('tipo_documento_identidad', 'identity_doc_type') || '').trim().toLowerCase();
    setIdentityViewDocType(saved || 'passport');
  }, [caseDetail]);
  const identityDocTypeSelector = useMemo(
    () => (
      <select
        value={identityViewDocType || 'passport'}
        onChange={(e) => setIdentityViewDocType(e.target.value)}
        className="w-full min-w-0 sm:min-w-[220px] text-sm border border-slate-200 rounded-lg px-2.5 py-2 sm:py-1.5 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400"
      >
        {identityViewDocTypeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ),
    [identityViewDocType, identityViewDocTypeOptions],
  );
  const personDisplayName = (...parts) => {
    const clean = parts
      .map((p) => String(p || '').trim())
      .filter((p) => p && p !== '—' && p !== 'â€”');
    return clean.length ? clean.join(' ') : '—';
  };
  const parseCount = (...rawValues) => {
    for (const raw of rawValues) {
      const n = Number.parseInt(String(raw ?? '').trim(), 10);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return 0;
  };
  const truthyLike = (raw) => {
    if (raw === true) return true;
    const s = String(raw ?? '').trim().toLowerCase();
    return ['true', '1', 'yes', 'si', 'sí', 'y'].includes(s);
  };
  const identityHistoryLabel = useMemo(() => {
    const identityType = String(collected.tipo_documento_identidad || c.tipo_documento_identidad || '')
      .trim()
      .toLowerCase();
    const hasPassport = truthyLike(collected.tiene_pasaporte ?? c.has_passport);
    return hasPassport || identityType === 'passport' ? 'Passport' : 'Other ID';
  }, [collected.tipo_documento_identidad, collected.tiene_pasaporte, c.tipo_documento_identidad, c.has_passport]);
  const normalizeHistoryTitle = useCallback(
    (title) => {
      const raw = String(title || '').trim();
      if (!raw) return raw;
      return raw.replace(/Passport\s*\/\s*ID/gi, identityHistoryLabel);
    },
    [identityHistoryLabel],
  );
  const localizedDocLabel = useCallback((label) => {
    const raw = String(label || '').trim();
    const normalized = raw.toLowerCase();
    if (!normalized) return raw;
    if (normalized === 'passport / id') return identityHistoryLabel;
    if (normalized === 'passport') return t('advisorPortal.docCat_passport_name');
    if (normalized === 'other id') return t('advisorPortal.historyDocOtherId');
    if (normalized === 'asylum documents') return t('advisorPortal.historyDocAsylumDocuments');
    if (normalized === 'criminal record certificate') return t('advisorPortal.historyDocCriminalRecordCertificate');
    if (normalized === 'proof of continuous residence') return t('advisorPortal.historyDocProofContinuousResidence');
    return raw;
  }, [identityHistoryLabel, t]);
  const localizeHistoryEvent = useCallback((event) => {
    if (!event) return event;
    const rawTitle = normalizeHistoryTitle(event.title);
    const rawBody = String(event.body || '');
    let title = rawTitle;
    let body = rawBody;

    const uploadedMatch = rawTitle.match(/^Document uploaded\s+[—-]\s+(.+)$/i);
    const replacedMatch = rawTitle.match(/^Document replaced\s+[—-]\s+(.+)$/i);
    const acceptedMatch = rawTitle.match(/^Document accepted\s+[—-]\s+(.+)$/i);
    const removedMatch = rawTitle.match(/^Document removed\s+[—-]\s+(.+)$/i);
    const archivedMatch = rawTitle.match(/^Document archived\s+[—-]\s+(.+)$/i);
    const restoredMatch = rawTitle.match(/^Document restored\s+[—-]\s+(.+)$/i);
    const assignedMatch = rawBody.match(/^Case assigned to\s+(.+)\.$/i);
    const acceptedBodyMatch = rawBody.match(/^(.+?) was accepted\.$/i);
    const fileBodyMatch = rawBody.match(/^File:\s+(.+)$/i);
    const fileActorMatch = rawBody.match(/^(.+?)\s+·\s+by\s+(.+)$/i);

    if (/^Lead qualification \(website funnel\)$/i.test(rawTitle)) {
      title = t('advisorPortal.qualificationTitle');
    } else if (replacedMatch) {
      title = `Document replaced — ${localizedDocLabel(replacedMatch[1])}`;
    } else if (uploadedMatch) {
      title = t('advisorPortal.historyDocumentUploaded', {
        document: localizedDocLabel(uploadedMatch[1]),
      });
    } else if (acceptedMatch) {
      title = t('advisorPortal.historyDocumentAccepted', {
        document: localizedDocLabel(acceptedMatch[1]),
      });
    } else if (removedMatch) {
      title = `Document deleted — ${localizedDocLabel(removedMatch[1])}`;
    } else if (archivedMatch) {
      title = `Document archived — ${localizedDocLabel(archivedMatch[1])}`;
    } else if (restoredMatch) {
      title = `Document restored — ${localizedDocLabel(restoredMatch[1])}`;
    } else if (/^Advisor assigned$/i.test(rawTitle)) {
      title = t('advisorPortal.historyAdvisorAssigned');
    } else if (/^Case opened$/i.test(rawTitle)) {
      title = t('advisorPortal.historyCaseOpened');
    } else if (/^Applicant help request$/i.test(rawTitle) || /^Help request\s+[—-]/i.test(rawTitle)) {
      title = t('advisorPortal.historyHelpRequest');
    } else if (/^Family application interest$/i.test(rawTitle)) {
      title = t('advisorPortal.historyFamilyApplicationInterest');
    } else if (/^(Responded|Replied) to applicant/i.test(rawTitle)) {
      title = t('advisorPortal.historyHelpReplied');
    } else if (/^Replied to family application interest$/i.test(rawTitle)) {
      title = t('advisorPortal.historyFamilyInterestReplied');
    } else if (/^Responded to family application interest$/i.test(rawTitle)) {
      title = t('advisorPortal.historyFamilyInterestResponded');
    }

    if (fileActorMatch) {
      body = `${fileActorMatch[1]} · by ${fileActorMatch[2]}`;
    } else if (acceptedBodyMatch) {
      body = t('advisorPortal.historyFileAccepted', { file: acceptedBodyMatch[1] });
    } else if (fileBodyMatch) {
      body = t('advisorPortal.historyFileLabel', { file: fileBodyMatch[1] });
    } else if (assignedMatch) {
      body = t('advisorPortal.historyCaseAssignedBody', { name: assignedMatch[1] });
    } else if (/^Application case created \(UNIFIED\)\. The applicant can continue in the chat or complete uploads through the portal\.$/i.test(rawBody)) {
      body = t('advisorPortal.historyCaseOpenedBody');
    }

    return { ...event, title, body };
  }, [localizedDocLabel, normalizeHistoryTitle, t]);

  const identityPairs = useMemo(() => ([
    [t('advisorPortal.fieldFirstName'), read('nombre', 'first_name')],
    [t('advisorPortal.fieldLastName'), read('apellidos', 'last_name')],
    [t('advisorPortal.fieldSecondSurname'), read('segundo_apellido', 'second_surname')],
    [t('advisorPortal.fieldDateOfBirth'), formatDateDmy(read('fecha_nacimiento', 'date_of_birth'))],
    [t('advisorPortal.fieldPlaceOfBirth'), read('lugar_nacimiento', 'place_of_birth')],
    [t('advisorPortal.fieldCountryOfBirth'), read('pais_nacimiento', 'country_of_birth')],
    [t('advisorPortal.fieldNationality'), read('nacionalidad', 'nationality')],
    [t('advisorPortal.fieldCivilStatus'), civilStatusDisplay(read('estado_civil', 'civil_status'))],
    [t('advisorPortal.fieldNie'), read('numero_nie', 'nie')],
    [t('advisorPortal.fieldSex'), sexLabel(read('sexo', 'gender'))],
  ]), [caseDetail, t]);
  const personalIdentityPairs = useMemo(() => ([
    [t('advisorPortal.fieldFirstName'), read('nombre', 'first_name')],
    [t('advisorPortal.fieldLastName'), read('apellidos', 'last_name')],
    [t('advisorPortal.fieldSecondSurname'), read('segundo_apellido', 'second_surname')],
    [t('advisorPortal.fieldDateOfBirth'), formatDateDmy(read('fecha_nacimiento', 'date_of_birth'))],
    [t('advisorPortal.fieldPlaceOfBirth'), read('lugar_nacimiento', 'place_of_birth')],
    [t('advisorPortal.fieldCountryOfBirth'), read('pais_nacimiento', 'country_of_birth')],
    [t('section6.nationalIdType', { defaultValue: 'Document type' }), identityDocTypeSelector],
    [t('section6.nationalIdNumber', { defaultValue: 'Document number' }), selectedIdentityDocumentNumber()],
    [t('advisorPortal.fieldNationality'), read('nacionalidad', 'nationality')],
    [t('advisorPortal.fieldSex'), sexLabel(read('sexo', 'gender'))],
  ]), [caseDetail, identityDocTypeSelector, selectedIdentityDocumentNumber, t]);
  const personalOtherPairs = useMemo(() => ([
    [t('advisorPortal.fieldCivilStatus'), civilStatusDisplay(read('estado_civil', 'civil_status'))],
    [t('advisorPortal.fieldNie'), read('numero_nie', 'nie')],
    [
      t('advisorPortal.familyFather'),
      c.father_not_known
        ? t('advisorPortal.unknownValue')
        : personDisplayName(
          read('father_first_name'),
          read('father_last_name', 'father_apellido1'),
          read('father_second_surname', 'father_apellido2'),
        ),
    ],
    [
      t('advisorPortal.familyMother'),
      c.mother_not_known
        ? t('advisorPortal.unknownValue')
        : personDisplayName(
          read('mother_first_name'),
          read('mother_last_name', 'mother_apellido1'),
          read('mother_second_surname', 'mother_apellido2'),
        ),
    ],
  ]), [c.father_not_known, c.mother_not_known, caseDetail, t]);

  const contactPairs = useMemo(() => ([
    [
      t('advisorPortal.fieldEmail'),
      {
        value: c.user_email || read('email'),
        verified: isTruthyFlag(c.email_verified),
      },
    ],
    [
      t('advisorPortal.fieldMobile'),
      {
        value: c.user_phone || read('telefono', 'phone'),
        verified: isTruthyFlag(c.phone_verified),
      },
    ],
    [t('advisorPortal.fieldChannel'), channelLabel(read('canal_contacto', 'preferred_contact_channel'))],
    [t('advisorPortal.fieldPreferredContact'), channelLabel(read('metodo_contacto_preferido', 'preferred_contact_method'))],
  ]), [caseDetail, t, c.email_verified, c.phone_verified]);

  const addressPairs = useMemo(() => ([
    [t('advisorPortal.fieldStreetAddress'), read('domicilio', 'direccion', 'street')],
    [t('advisorPortal.fieldNumber'), read('numero_domicilio')],
    [t('advisorPortal.fieldFloor'), read('piso')],
    [t('advisorPortal.fieldDoor'), read('puerta')],
    [t('advisorPortal.fieldCity'), read('localidad', 'ciudad', 'city')],
    [t('advisorPortal.fieldProvince'), read('provincia', 'province')],
    [t('advisorPortal.fieldPostalCode'), read('codigo_postal', 'postal_code')],
  ]), [caseDetail, t]);

  const representativePairs = useMemo(() => {
    const col = caseDetail?.case?.collected_fields || {};
    return [
      [t('advisorPortal.fieldFirmName', { defaultValue: 'Firm Name' }), displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'empresa_representante')],
      [
        t('advisorPortal.fieldDniNieCif', { defaultValue: 'DNI/NIE/CIF' }),
        displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'documento_representante'),
      ],
      [t('advisorPortal.fieldAddress', { defaultValue: 'Address' }), displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'domicilio_representante')],
      [
        t('advisorPortal.fieldNumber'),
        displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'numero_domicilio_representante'),
      ],
      [t('advisorPortal.fieldFloor'), displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'piso_representante')],
      [t('advisorPortal.fieldLocality', { defaultValue: 'Locality' }), displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'localidad_representante')],
      [
        t('advisorPortal.fieldPostalCode'),
        displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'codigo_postal_representante'),
      ],
      [t('advisorPortal.fieldProvince'), displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'provincia_representante')],
      [t('advisorPortal.fieldMobile', { defaultValue: 'Mobile' }), displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'telefono_representante')],
      [t('advisorPortal.fieldEmail'), displayCollectedLine(col, DEFAULT_REPRESENTATIVE_COLLECTED, 'email_representante')],
    ];
  }, [caseDetail, t]);

  const notificationPairs = useMemo(() => {
    const col = caseDetail?.case?.collected_fields || {};
    return [
      [t('advisorPortal.fieldName'), displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'nombre_notificaciones')],
      [
        t('advisorPortal.fieldDocumentId'),
        displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'documento_notificaciones'),
      ],
      [t('advisorPortal.fieldStreet'), displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'domicilio_notificaciones')],
      [
        t('advisorPortal.fieldNumber'),
        displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'numero_domicilio_notificaciones'),
      ],
      [t('advisorPortal.fieldFloor'), displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'piso_notificaciones')],
      [t('advisorPortal.fieldCity'), displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'localidad_notificaciones')],
      [
        t('advisorPortal.fieldPostalCode'),
        displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'codigo_postal_notificaciones'),
      ],
      [t('advisorPortal.fieldProvince'), displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'provincia_notificaciones')],
      [t('advisorPortal.fieldPhone'), displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'telefono_notificaciones')],
      [t('advisorPortal.fieldEmail'), displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'email_notificaciones')],
      [
        t('advisorPortal.fieldElectronicNotifications'),
        formatElectronicNotificationsDisplay(
          displayCollectedLine(col, DEFAULT_NOTIFICATION_COLLECTED, 'notificaciones_electronicas'),
        ),
      ],
    ];
  }, [caseDetail, t]);

  const SECTION_FIELD_MAP = {
    identity: {
      firstName: 'nombre',
      lastName: 'apellidos',
      secondSurname: 'segundo_apellido',
      dob: 'fecha_nacimiento',
      arrivalDate: 'fecha_llegada',
      placeOfBirth: 'lugar_nacimiento',
      countryOfBirth: 'pais_nacimiento',
      docType: 'tipo_documento_identidad',
      passportNumber: 'passport_number',
      nationalIdNumber: 'national_id_document_number',
      travelDocumentNumber: 'travel_document_number',
      registrationDocumentNumber: 'registration_document_number',
      otherDocumentNumber: 'other_document_number',
      nationality: 'nacionalidad',
      sex: 'sexo',
      civilStatus: 'estado_civil',
      nie: 'numero_nie',
      fatherName: 'father_first_name',
      motherName: 'mother_first_name',
    },
    contact: {
      mobile: 'telefono',
      email: 'email',
      channel: 'canal_contacto',
      preferredContact: 'metodo_contacto_preferido',
    },
    address: {
      street: 'domicilio',
      number: 'numero_domicilio',
      floor: 'piso',
      door: 'puerta',
      city: 'localidad',
      province: 'provincia',
      postalCode: 'codigo_postal',
      country: 'pais_residencia',
      residentSince: 'residente_desde',
    },
    representative: {
      companyName: 'empresa_representante',
      documentId: 'documento_representante',
      street: 'domicilio_representante',
      number: 'numero_domicilio_representante',
      floor: 'piso_representante',
      city: 'localidad_representante',
      postalCode: 'codigo_postal_representante',
      province: 'provincia_representante',
      phone: 'telefono_representante',
      email: 'email_representante',
    },
    notification: {
      name: 'nombre_notificaciones',
      documentId: 'documento_notificaciones',
      street: 'domicilio_notificaciones',
      number: 'numero_domicilio_notificaciones',
      floor: 'piso_notificaciones',
      city: 'localidad_notificaciones',
      postalCode: 'codigo_postal_notificaciones',
      province: 'provincia_notificaciones',
      phone: 'telefono_notificaciones',
      email: 'email_notificaciones',
      electronic: 'notificaciones_electronicas',
    },
  };

  const validationMessageForField = useCallback((section, key, value) => {
    if (section === 'identity' || section === 'other') {
      if (key === 'firstName' && !isValidName40(value, false)) return t('advisorPortal.validationFirstName');
      if (key === 'lastName' && !isValidName40(value, false)) return t('advisorPortal.validationLastName');
      if (key === 'secondSurname' && !isValidName40(value, false)) return t('advisorPortal.validationSecondSurname');
      if (key === 'dob' && String(value || '').trim() && !isValidAdultDobRule(value)) return t('advisorPortal.validationDobAdult');
      if (key === 'placeOfBirth' && !isValidCityRule(value, false)) return t('advisorPortal.validationPlaceOfBirth');
      if (key === 'fatherName' && !isValidName80(value, false)) return t('advisorPortal.validationFatherName');
      if (key === 'motherName' && !isValidName80(value, false)) return t('advisorPortal.validationMotherName');
      if (key === 'passportNumber' && !isValidPassportRule(value, false)) return t('advisorPortal.validationPassport');
      if (key === 'nie') {
        const nieValue = String(value || '').trim().toUpperCase();
        // Avoid showing "invalid NIE" while the user is still typing.
        if (nieValue && nieValue.length >= 9 && !isValidNieRule(nieValue, false)) {
          return t('advisorPortal.validationNie');
        }
      }
    }
    if (section === 'contact') {
      if (key === 'mobile' && !isValidPhoneRule(value, false)) return t('advisorPortal.validationPhone');
      if (key === 'email' && !isValidEmailRule(value, false)) return t('advisorPortal.validationEmail');
    }
    if (section === 'address') {
      if (key === 'street' && !isValidStreetRule(value, false)) return t('advisorPortal.validationStreet');
      if (key === 'number' && !isValidStreetNumberRule(value, false)) return t('advisorPortal.validationStreetNumber');
      if (key === 'city' && !isValidCityRule(value, false)) return t('advisorPortal.validationCity');
      if (key === 'postalCode' && !isValidPostalCodeRule(value, false)) return t('advisorPortal.validationPostalCode');
    }
    if (section === 'representative') {
      if (key === 'phone' && !isValidPhoneRule(value, false)) return t('advisorPortal.validationRepresentativePhone');
      if (key === 'email' && !isValidEmailRule(value, false)) return t('advisorPortal.validationRepresentativeEmail');
      if (key === 'postalCode' && !isValidPostalCodeRule(value, false)) {
        return t('advisorPortal.saveRepresentativePostalCode5');
      }
    }
    if (section === 'notification') {
      if (key === 'phone' && !isValidPhoneRule(value, false)) return t('advisorPortal.validationNotificationPhone');
      if (key === 'email' && !isValidEmailRule(value, false)) return t('advisorPortal.validationNotificationEmail');
      if (key === 'postalCode' && !isValidPostalCodeRule(value, false)) {
        return t('advisorPortal.saveNotificationPostalCode5');
      }
    }
    return null;
  }, [t]);

  const recentActivity = useMemo(
    () => historyPreviewFull.slice(0, 4).map((event) => localizeHistoryEvent(event)),
    [historyPreviewFull, localizeHistoryEvent],
  );
  const hasPendingCustomerMessages = useMemo(
    () => hasPendingHelpMessages(historyPreviewFull),
    [historyPreviewFull],
  );
  const firstPendingCustomerMessage = useMemo(
    () => getFirstPendingHelpMessage(historyPreviewFull),
    [historyPreviewFull],
  );

  const handleOpenPendingCustomerMessage = useCallback(async () => {
    setHelpReplyText('');
    setActiveTab('activity');
    if (historyFilter !== 'all') {
      setHistoryFilter('all');
    }
    await loadHistoryTab('all');
    const pendingMessage = firstPendingCustomerMessage;
    if (pendingMessage) {
      setHelpMessageModal(pendingMessage);
    }
  }, [firstPendingCustomerMessage, historyFilter, loadHistoryTab]);

  const handlePersistHideCustomerBanner = useCallback(async () => {
    if (confirmHideCustomerBannerSaving) return;
    setConfirmHideCustomerBannerSaving(true);
    try {
      await mvpApi.patchCaseExtensions(userId, sessionId, {
        advisor_hide_customer_message_banner: true,
      });
      setShowCustomerMessageBanner(false);
      setConfirmHideCustomerBannerOpen(false);
      setToast('Customer message banner hidden');
      await refreshCase();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setToast(typeof raw === 'string' ? raw : t('advisorPortal.saveSectionFailed'));
    } finally {
      setConfirmHideCustomerBannerSaving(false);
    }
  }, [confirmHideCustomerBannerSaving, refreshCase, sessionId, t, userId]);

  const qualificationDisplay = useMemo(() => {
    const qr = c.qualification_responses;
    if (!qr || typeof qr !== 'object') return null;
    const answers = Array.isArray(qr.answers) ? qr.answers : [];
    const hasContent =
      answers.length > 0 || Boolean(qr.outcome) || Boolean(qr.flow) || Boolean(qr.lastUpdatedAt || qr.startedAt);
    if (!hasContent) return null;
    const flow = qr.flow ? String(qr.flow) : null;
    const outcome = qr.outcome != null && String(qr.outcome).trim() !== '' ? String(qr.outcome) : null;
    const qaEntries = answers.map((a) => {
      const question = a.questionText || a.questionId || t('advisorPortal.qualificationQuestionFallback');
      const answer =
        a.answer === 'yes' ? 'Yes' : a.answer === 'no' ? 'No' : String(a.answer ?? '—');
      return { question, answer };
    });
    const snapshotAt =
      (typeof qr.lastUpdatedAt === 'string' && qr.lastUpdatedAt) ||
      (typeof qr.startedAt === 'string' && qr.startedAt) ||
      (typeof c.created_at === 'string' && c.created_at) ||
      null;
    return { answers, flow, outcome, qaEntries, snapshotAt };
  }, [c.qualification_responses, c.created_at]);

  /** Merge registration answers into History for All + Activity (system-generated). */
  const historyDisplayList = useMemo(() => {
    const includeQualification =
      (historyFilter === 'activity' || historyFilter === 'all') &&
      qualificationDisplay &&
      (qualificationDisplay.flow ||
        qualificationDisplay.outcome ||
        (qualificationDisplay.qaEntries && qualificationDisplay.qaEntries.length > 0));
    if (!includeQualification) return history;
    const synthetic = {
      id: `lead-qualification:${userId}:${sessionId}`,
      kind: 'lead_qualification',
      title: t('advisorPortal.qualificationTitle'),
      at: qualificationDisplay.snapshotAt,
      actor_name: null,
      flow: qualificationDisplay.flow,
      outcome: qualificationDisplay.outcome,
      qaEntries: qualificationDisplay.qaEntries,
    };
    const merged = [...history, synthetic];
    merged.sort((a, b) => {
      const ta = new Date(a.at || 0).getTime();
      const tb = new Date(b.at || 0).getTime();
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    });
    return merged.map((event) => localizeHistoryEvent(event));
  }, [history, historyFilter, qualificationDisplay, userId, sessionId, t, localizeHistoryEvent]);

  const showDraftBanner = String(c.status || '').toLowerCase() !== 'submitted';

  /** EX-10 PDF: watermark + Draft UX when not submitted, fields missing, or completion under 100%. */
  const ex10PdfIsDraft = useMemo(() => {
    const notSubmitted = String(c.status || '').toLowerCase() !== 'submitted';
    if (notSubmitted) return true;
    const mf = c.missing_fields;
    if (Array.isArray(mf) && mf.length > 0) return true;
    const pct = Number(c.completion_percentage);
    if (!Number.isNaN(pct) && pct < 99.5) return true;
    return false;
  }, [c.status, c.missing_fields, c.completion_percentage]);

  /** Match backend ``_resolve_ex_form_code`` (session + collected_fields) so EX-31 HTML export gets form_code=EX-31. */
  const isAsylumCase = useMemo(() => {
    const col = c.collected_fields || {};
    const raw =
      c.asylum_applicant ??
      col.asylum_applicant ??
      c.solicitante_asilo ??
      col.solicitante_asilo ??
      c.asylum_routing_confirmed ??
      col.asylum_routing_confirmed ??
      c.prior_asylum_application ??
      col.prior_asylum_application;
    if (raw === true) return true;
    const normalized = String(raw || '').trim().toLowerCase();
    return normalized === 'yes' || normalized === 'true' || normalized === '1';
  }, [
    c.asylum_applicant,
    c.collected_fields,
    c.solicitante_asilo,
    c.asylum_routing_confirmed,
    c.prior_asylum_application,
  ]);
  const advisorFormCode = isAsylumCase ? 'EX-31' : 'EX-32';

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const beginEditDraft = (draft) => {
    setEditDraft(draft);
    setInitialEditDraft(draft);
    setEditedFields(new Set());
    setFieldErrors({});
  };

  const markFieldEdited = (key) => {
    setEditedFields((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const clearEditedField = useCallback((key) => {
    setEditedFields((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const persistVerifiedContactField = useCallback(
    async (field, value) => {
      const normalized = String(value || '').trim();
      if (!normalized) throw new Error('Value is required');
      const payload = field === 'email' ? { email: normalized } : { phone: normalized };
      await mvpApi.patchAdminCustomerDetails(userId, payload);
      await refreshCase();
      if (field === 'email') {
        setEditDraft((prev) => ({ ...prev, email: normalized }));
        setInitialEditDraft((prev) => ({ ...prev, email: normalized }));
        clearEditedField('email');
      } else {
        setEditDraft((prev) => ({ ...prev, mobile: normalized }));
        setInitialEditDraft((prev) => ({ ...prev, mobile: normalized }));
        clearEditedField('mobile');
      }
    },
    [clearEditedField, refreshCase, userId]
  );

  const startEditing = (section) => {
    setEditingSection(section);
    if (section === 'identity') {
      beginEditDraft({
        firstName: read('nombre', 'first_name') === '—' ? '' : read('nombre', 'first_name'),
        lastName: read('apellidos', 'last_name') === '—' ? '' : read('apellidos', 'last_name'),
        secondSurname: read('segundo_apellido', 'second_surname') === '—' ? '' : read('segundo_apellido', 'second_surname'),
        dob: (() => {
          const raw = read('fecha_nacimiento', 'date_of_birth') === '—' ? '' : read('fecha_nacimiento', 'date_of_birth');
          return birthDateToHtmlInputValue(raw) || '';
        })(),
        arrivalDate: (() => {
          const raw = read('fecha_llegada', 'arrival_date') === '—' ? '' : read('fecha_llegada', 'arrival_date');
          return birthDateToHtmlInputValue(raw) || String(raw || '');
        })(),
        placeOfBirth: read('lugar_nacimiento', 'place_of_birth') === '—' ? '' : read('lugar_nacimiento', 'place_of_birth'),
        countryOfBirth: read('pais_nacimiento', 'country_of_birth') === '—' ? '' : read('pais_nacimiento', 'country_of_birth'),
        nationality: read('nacionalidad', 'nationality') === '—' ? '' : read('nacionalidad', 'nationality'),
        docType: read('tipo_documento_identidad', 'identity_doc_type') === 'â€”' ? '' : read('tipo_documento_identidad', 'identity_doc_type'),
        passportNumber: read('passport_number', 'numero_pasaporte', 'identity_doc_number') === 'â€”' ? '' : read('passport_number', 'numero_pasaporte', 'identity_doc_number'),
        nationalIdNumber: read('national_id_document_number', 'numero_documento_identidad', 'identity_document_number') === 'â€”' ? '' : read('national_id_document_number', 'numero_documento_identidad', 'identity_document_number'),
        travelDocumentNumber: read('travel_document_number') === 'â€”' ? '' : read('travel_document_number'),
        registrationDocumentNumber: read('registration_document_number') === 'â€”' ? '' : read('registration_document_number'),
        otherDocumentNumber: read('other_document_number') === 'â€”' ? '' : read('other_document_number'),
        sex: canonicalApplicantSex(read('sexo', 'gender')),
        civilStatus: normalizeCivilStatusValue(read('estado_civil', 'civil_status')),
        nie: read('nie', 'numero_nie') === '—' ? '' : read('nie', 'numero_nie'),
        fatherName: read('father_first_name') === '—' ? '' : read('father_first_name'),
        motherName: read('mother_first_name') === '—' ? '' : read('mother_first_name'),
      });
    }
    if (section === 'contact') {
      const mobileSeed = c.user_phone || (read('telefono', 'phone') === '—' ? '' : read('telefono', 'phone'));
      const seededChannel = channelLabel(read('canal_contacto', 'preferred_contact_channel'));
      setContactMobileCountryCode(detectDialCode(mobileSeed, '+34'));
      setContactMobileChannel('SMS');
      setContactMobileVerified(isTruthyFlag(c.phone_verified));
      setContactEmailVerified(isTruthyFlag(c.email_verified));
      setContactEmailVerifying(false);
      setContactEmailCode('');
      beginEditDraft({
        mobile: mobileSeed,
        email: c.user_email || (read('email') === '—' ? '' : read('email')),
        channel: read('canal_contacto', 'preferred_contact_channel') === '—' ? '' : read('canal_contacto', 'preferred_contact_channel'),
        preferredContact: read('metodo_contacto_preferido', 'preferred_contact_method') === '—' ? '' : read('metodo_contacto_preferido', 'preferred_contact_method'),
      });
    }
    if (section === 'address') {
      beginEditDraft({
        street: read('domicilio', 'direccion', 'street') === '—' ? '' : read('domicilio', 'direccion', 'street'),
        number: read('numero_domicilio') === '—' ? '' : read('numero_domicilio'),
        floor: read('piso') === '—' ? '' : read('piso'),
        door: read('puerta') === '—' ? '' : read('puerta'),
        city: read('localidad', 'ciudad', 'city') === '—' ? '' : read('localidad', 'ciudad', 'city'),
        province: read('provincia') === '—' ? '' : read('provincia'),
        postalCode: read('codigo_postal', 'postal_code') === '—' ? '' : read('codigo_postal', 'postal_code'),
        country: read('pais_residencia', 'pais', 'country') === '—' ? '' : read('pais_residencia', 'pais', 'country'),
        residentSince: read('residente_desde') === '—' ? '' : read('residente_desde'),
      });
    }
    if (section === 'representative') {
      const R = DEFAULT_REPRESENTATIVE_COLLECTED;
      const m = SECTION_FIELD_MAP.representative;
      beginEditDraft({
        companyName: effectiveStringForEdit(collected, R, m.companyName),
        documentId: effectiveStringForEdit(collected, R, m.documentId),
        street: effectiveStringForEdit(collected, R, m.street),
        number: effectiveStringForEdit(collected, R, m.number),
        floor: effectiveStringForEdit(collected, R, m.floor),
        city: effectiveStringForEdit(collected, R, m.city),
        postalCode: effectiveStringForEdit(collected, R, m.postalCode),
        province: effectiveStringForEdit(collected, R, m.province),
        phone: effectiveStringForEdit(collected, R, m.phone),
        email: effectiveStringForEdit(collected, R, m.email),
      });
    }
    if (section === 'notification') {
      const N = DEFAULT_NOTIFICATION_COLLECTED;
      const m = SECTION_FIELD_MAP.notification;
      beginEditDraft({
        name: effectiveStringForEdit(collected, N, m.name),
        documentId: effectiveStringForEdit(collected, N, m.documentId),
        street: effectiveStringForEdit(collected, N, m.street),
        number: effectiveStringForEdit(collected, N, m.number),
        floor: effectiveStringForEdit(collected, N, m.floor),
        city: effectiveStringForEdit(collected, N, m.city),
        postalCode: effectiveStringForEdit(collected, N, m.postalCode),
        province: effectiveStringForEdit(collected, N, m.province),
        phone: effectiveStringForEdit(collected, N, m.phone),
        email: effectiveStringForEdit(collected, N, m.email),
        electronic: effectiveStringForEdit(collected, N, m.electronic),
      });
    }
  };

  const saveSection = async () => {
    if (!editingSection) return;
    const map = SECTION_FIELD_MAP[editingSection] || {};
    setSavingSection(true);
    try {
      const updates = Object.entries(editDraft).filter(([key, v]) => {
        if (!SECTION_FIELD_MAP[editingSection]?.[key]) return false;
        if (!editedFields.has(key)) return false;
        const nextValue = String(v || '').trim();
        const prevValue = String(initialEditDraft?.[key] || '').trim();
        return nextValue !== prevValue;
      });
      if (!updates.length) {
        setToast(t('advisorPortal.saveSectionDone'));
        setEditingSection(null);
        setEditDraft({});
        setInitialEditDraft({});
        setEditedFields(new Set());
        setFieldErrors({});
        return;
      }
      for (const [key, value] of updates) {
        const validationMessage = validationMessageForField(editingSection, key, value);
        if (validationMessage) {
          setFieldErrors({ [key]: validationMessage });
          setToast(validationMessage);
          setSavingSection(false);
          return;
        }
      }
      if (editingSection === 'address') {
        const postal = String(editDraft.postalCode || '').trim();
        if (postal && !/^\d{5}$/.test(postal)) {
          setToast(t('advisorPortal.savePostalCode5'));
          setSavingSection(false);
          return;
        }
      }
      if (editingSection === 'representative') {
        const postal = String(editDraft.postalCode || '').trim();
        if (postal && !/^\d{5}$/.test(postal)) {
          setToast(t('advisorPortal.saveRepresentativePostalCode5'));
          setSavingSection(false);
          return;
        }
      }
      if (editingSection === 'notification') {
        const postal = String(editDraft.postalCode || '').trim();
        if (postal && !/^\d{5}$/.test(postal)) {
          setToast(t('advisorPortal.saveNotificationPostalCode5'));
          setSavingSection(false);
          return;
        }
      }
      if (editingSection === 'contact') {
        const payload = {};
        if (editedFields.has('email')) {
          const email = String(editDraft.email || '').trim();
          payload.email = email || undefined;
        }
        if (editedFields.has('mobile')) {
          const phone = String(editDraft.mobile || '').trim();
          payload.phone = phone || undefined;
        }
        if (editedFields.has('channel')) {
          const channelRaw = String(editDraft.channel || contactMobileChannel || '').trim().toLowerCase();
          payload.preferred_contact_channel = 'sms';
        }
        if (editedFields.has('preferredContact')) {
          const preferredRaw = String(editDraft.preferredContact || '').trim().toLowerCase();
          payload.email_primary = preferredRaw === 'email';
        }
        await mvpApi.patchAdminCustomerDetails(userId, payload);

        setToast(t('advisorPortal.saveSectionDone'));
        await refreshCase();
        setEditingSection(null);
        setEditDraft({});
        setInitialEditDraft({});
        setEditedFields(new Set());
        setFieldErrors({});
        return;
      }
      const fieldPayload = {};
      for (const [key, value] of updates) {
        const apiField = map[key];
        if (!apiField) continue;
        let valueToSave = value;
        if (editingSection === 'identity' && key === 'civilStatus') {
          valueToSave = normalizeCivilStatusValue(value);
        }
        if (editingSection === 'identity' && key === 'sex') {
          valueToSave = canonicalApplicantSex(value);
        }
        fieldPayload[apiField] = valueToSave;
      }
      const bulkRes = await mvpApi.updateCaseFields(userId, sessionId, fieldPayload);
      const failedFields = bulkRes?.data?.failed_fields || {};
      const savedCount = Object.keys(bulkRes?.data?.saved_fields || {}).length;
      const failedKeys = Object.keys(failedFields);
      if (failedKeys.length) {
        setToast(t('advisorPortal.savePartial', { count: savedCount, reason: `${failedKeys[0]}: ${failedFields[failedKeys[0]]}` }));
      } else {
        setToast(t('advisorPortal.saveSectionDone'));
      }
      await refreshCase();
      setEditingSection(null);
      setEditDraft({});
      setInitialEditDraft({});
      setEditedFields(new Set());
      setFieldErrors({});
    } catch (e) {
      const msg = e?.response?.data?.detail?.error || e?.response?.data?.detail || e?.message;
      setToast(typeof msg === 'string' ? msg : t('advisorPortal.saveSectionFailed'));
    } finally {
      setSavingSection(false);
    }
  };

  const requestContactPhoneOtp = useCallback(async () => {
    const normalizedPhone = String(editDraft.mobile || '').replace(/\s+/g, '').trim();
    if (!normalizedPhone) throw new Error('Phone number is required');
    const channel = 'sms';
    const resp = await mvpApi.adminRequestPhoneOtp(normalizedPhone, channel);
    return resp?.data;
  }, [editDraft.mobile, contactMobileChannel]);

  const verifyContactPhoneOtp = useCallback(async (code) => {
    const normalizedPhone = String(editDraft.mobile || '').replace(/\s+/g, '').trim();
    if (!normalizedPhone) throw new Error('Phone number is required');
    await mvpApi.adminVerifyPhoneOtp(normalizedPhone, code);
    await persistVerifiedContactField('phone', normalizedPhone);
  }, [editDraft.mobile, persistVerifiedContactField]);

  const runReferralLookup = useCallback(async () => {
    const code = referralInput.trim().toUpperCase();
    if (!code) {
      setReferralLookup(null);
      return;
    }
    try {
      const res = await mvpApi.lookupReferralCode(code);
      const d = res?.data;
      if (d?.found) {
        if (d.user_id != null && String(d.user_id) === String(userId)) {
          setReferralLookup({ found: false, self: true });
          return;
        }
        setReferralLookup({
          found: true,
          user_id: d.user_id ?? null,
          name: d.name || '',
          entity_type: d.entity_type || (d.user_id != null ? 'user' : 'partner'),
          code: d.code || code,
        });
      } else {
        setReferralLookup({ found: false });
      }
    } catch {
      setReferralLookup({ found: false });
    }
  }, [referralInput, userId]);

  const saveReferralAttachment = async () => {
    if (!referralLookup?.found) return;
    const refUid = referralLookup?.user_id ?? referralLookup?.userId ?? null;
    setReferralSaving(true);
    try {
      await mvpApi.patchCaseExtensions(userId, sessionId, {
        case_referral_code: referralInput.trim().toUpperCase(),
        case_referral_ref_user_id: refUid == null ? null : Number(refUid),
        case_referral_ref_name: referralLookup.name || null,
      });
      setToast(t('advisorPortal.referralSaved'));
      setReferralEditing(false);
      setReferralInput('');
      setReferralLookup(null);
      await afterCaseExtensionSave();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setToast(typeof raw === 'string' ? raw : t('advisorPortal.referralSaveFailed'));
    } finally {
      setReferralSaving(false);
    }
  };

  const clearReferralAttachment = async () => {
    setReferralSaving(true);
    try {
      await mvpApi.patchCaseExtensions(userId, sessionId, { clear_case_referral: true });
      setToast(t('advisorPortal.referralRemoved'));
      setReferralEditing(false);
      setReferralInput('');
      setReferralLookup(null);
      await afterCaseExtensionSave();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setToast(typeof raw === 'string' ? raw : t('advisorPortal.referralRemoveFailed'));
    } finally {
      setReferralSaving(false);
    }
  };


  const statusNorm = String(c.status || '').toLowerCase().trim();
  const statusForWorkflow = ['new', 'in_progress', 'ready_to_submit', 'submitted'].includes(statusNorm)
    ? statusNorm
    : 'new';
  const requiredFieldsComplete = Array.isArray(c.missing_fields)
    ? c.missing_fields.length === 0
    : Number(c.completion_percentage || 0) >= 100;
  const requiredDocsUploaded = mergedDocSlots.length === 0 || mergedDocSlots.every((slot) => Boolean(slot.uploaded));
  const workflowReadyToSubmit = requiredFieldsComplete && requiredDocsUploaded;
  const canMarkSubmitted = statusForWorkflow === 'ready_to_submit' && workflowReadyToSubmit;

  const setCaseStatus = useCallback(async (nextStatus, opts = {}) => {
    const { silent = false } = opts;
    setStatusSaving(true);
    try {
      await mvpApi.patchCaseStatus(userId, sessionId, nextStatus);
      await refreshCase();
      if (!silent) {
        setToast(t('advisorPortal.caseTypeUpdated', { defaultValue: 'Status updated' }));
      }
      return true;
    } catch (err) {
      if (!silent) {
        const raw = err?.response?.data?.detail;
        setToast(typeof raw === 'string' ? raw : t('advisorPortal.caseSubmitFailed', { defaultValue: 'Failed to update status' }));
      }
      return false;
    } finally {
      setStatusSaving(false);
    }
  }, [refreshCase, sessionId, t, userId]);

  useEffect(() => {
    if (!workflowReadyToSubmit) return;
    if (!['new', 'in_progress'].includes(statusForWorkflow)) return;
    void setCaseStatus('ready_to_submit', { silent: true });
  }, [workflowReadyToSubmit, statusForWorkflow, setCaseStatus]);

  const onStatusChange = async (nextStatus) => {
    if (!nextStatus || nextStatus === statusForWorkflow) return;
    if (nextStatus === 'ready_to_submit' && !workflowReadyToSubmit) {
      setToast(t('advisorPortal.caseSubmitFailed', { defaultValue: 'Complete required fields and uploads first' }));
      return;
    }
    if (nextStatus === 'submitted' && !workflowReadyToSubmit) {
      setToast(t('advisorPortal.caseSubmitFailed', { defaultValue: 'Complete required fields and uploads first' }));
      return;
    }
    await setCaseStatus(nextStatus);
  };

  const markSubmitted = async () => {
    const ok = await setCaseStatus('submitted');
    if (ok) setToast(t('advisorPortal.caseSubmitted', { defaultValue: 'Case marked as submitted' }));
  };
  const initialCasePathway = useMemo(() => {
    if (c.asylum_applicant === true) return 'asylum';
    const raw = String(c.qualifying_ground || c.irregular_sub_route || c.sub_route || '').trim().toLowerCase();
    if (raw === 'work' || raw === '7a') return 'work';
    if (raw === 'family' || raw === '7b') return 'family';
    if (raw === 'vulnerability' || raw === '7c') return 'vulnerability';
    return '';
  }, [c]);
  const saveCaseType = async (pathway) => {
    setCaseTypeSaving(true);
    try {
      const isAsylum = pathway === 'asylum';
      const subRoute = pathway === 'work' ? '7A' : pathway === 'family' ? '7B' : pathway === 'vulnerability' ? '7C' : '';
      await mvpApi.updateCaseFields(userId, sessionId, {
        asylum_applicant: isAsylum,
        asylum_routing_confirmed: isAsylum,
        sub_route: isAsylum ? '' : subRoute,
        irregular_sub_route: isAsylum ? '' : (pathway || ''),
        qualifying_ground: isAsylum ? '' : (pathway || ''),
      });
      await refreshCase();
      setToast(t('advisorPortal.caseTypeUpdated'));
      setShowCaseTypeEditor(false);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setToast(typeof raw === 'string' ? raw : t('advisorPortal.caseTypeUpdateFailed'));
    } finally {
      setCaseTypeSaving(false);
    }
  };

  if (caseResolve.status === 'resolving' && !location.state?.userName) {
    return <SkeletonCaseDetail />;
  }
  if (caseResolve.status === 'error' || !userId || !sessionId) {
    if (caseResolve.status === 'resolving') return <SkeletonCaseDetail />; // still waiting for id
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <TopNav advisorName={t('advisorPortal.advisorNameFallback')} />
        <main className="flex-1 flex flex-col items-center justify-center">
          <p className="text-slate-600 mb-3">Case not found.</p>
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm font-medium border border-slate-200 text-slate-700 bg-white px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
            onClick={goBackToCasesList}
          >
            {t('advisorPortal.breadcrumbBack')}
          </button>
        </main>
      </div>
    );
  }

  if (loading && !location.state?.userName) {
    return <SkeletonCaseDetail />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col min-w-0 max-w-[100vw] overflow-x-hidden">
      <TopNav advisorName={advisorName} />
      {showCaseTypeEditor ? (
        <CaseTypeEditor
          initialPathway={initialCasePathway}
          saving={caseTypeSaving}
          onSave={saveCaseType}
          onClose={() => setShowCaseTypeEditor(false)}
        />
      ) : null}
      <main className="flex-1 w-full min-w-0 max-w-screen-xl mx-auto px-3 py-4 sm:px-5 sm:py-6 lg:px-8 lg:py-8">
        <div className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1 text-xs text-slate-400 mb-4 sm:mb-5">
          <button onClick={goBackToCasesList} type="button" className="flex min-h-[36px] shrink-0 items-center gap-1 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer font-medium whitespace-nowrap mr-1">
            <i className="ri-arrow-left-line text-sm" /> {t('advisorPortal.breadcrumbBack')}
          </button>
          <span className="text-slate-300">|</span>
          <Link to={casesListReturnTo} className="shrink-0 hover:text-slate-700 transition-colors cursor-pointer">{t('advisorPortal.breadcrumbApplications')}</Link>
          <i className="ri-arrow-right-s-line shrink-0" />
          {c.user_name || location.state?.userName ? (
            <>
              {c.user_id || location.state?.userId ? (
                <Link
                  to={`/customer/${c.user_id || location.state?.userId}`}
                  className="shrink-0 hover:text-slate-700 transition-colors cursor-pointer"
                >
                  {c.user_name || location.state?.userName}
                </Link>
              ) : (
                <span className="shrink-0 hover:text-slate-700 transition-colors cursor-default">
                  {c.user_name || location.state?.userName}
                </span>
              )}
              <i className="ri-arrow-right-s-line shrink-0" />
            </>
          ) : null}
          <span className="text-slate-600 font-medium font-mono whitespace-nowrap">{caseId}</span>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-5 min-w-0">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold text-slate-900 flex flex-wrap items-baseline gap-x-2 gap-y-1 min-w-0">
              {c.user_id || location.state?.userId ? (
                <Link
                  to={`/customer/${c.user_id || location.state?.userId}`}
                  className="min-w-0 break-words hover:text-slate-700 transition-colors cursor-pointer"
                >
                  {c.user_name || location.state?.userName || t('advisorPortal.caseLabelFallback')}
                </Link>
              ) : (
                <span className="min-w-0 break-words">
                  {c.user_name || location.state?.userName || t('advisorPortal.caseLabelFallback')}
                </span>
              )}
              <span className="text-slate-400 font-normal font-mono text-sm sm:text-base break-all">({caseId})</span>
            </h1>
          </div>
          <div className="grid w-full grid-cols-1 gap-2 min-[380px]:grid-cols-2 sm:flex sm:w-auto sm:items-center sm:flex-wrap sm:justify-end">
            <button
              type="button"
              onClick={() => setGenerateFormOpen(true)}
              className="inline-flex min-h-[44px] items-center justify-center gap-1.5 text-sm font-medium border border-slate-200 text-slate-700 bg-white px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-text-line text-sm" /> {t('advisorPortal.generateForm')}
            </button>
            <button
              type="button"
              onClick={() => setDownloadPackageOpen(true)}
              className="inline-flex min-h-[44px] items-center justify-center gap-1.5 text-sm font-medium border border-slate-200 text-slate-700 bg-white px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-folder-zip-line text-sm" /> {t('advisorPortal.downloadPackage')}
            </button>
          </div>
        </div>

        {!loading && showDraftBanner && showCustomerMessageBanner && hasPendingCustomerMessages ? (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-4">
            <i className="ri-message-2-line text-amber-600 text-sm shrink-0" />
            <button
              type="button"
              onClick={() => { void handleOpenPendingCustomerMessage(); }}
              className="text-left text-xs text-amber-800 font-medium flex-1 min-w-0 hover:text-amber-900 cursor-pointer"
            >
              New message from customer.
            </button>
            <button
              type="button"
              onClick={() => setConfirmHideCustomerBannerOpen(true)}
              className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded hover:bg-amber-100 text-amber-700 hover:text-amber-900 transition-colors cursor-pointer"
              aria-label="Dismiss customer message banner"
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>
        ) : null}

        <div className="flex min-w-0 items-center gap-1 overflow-x-auto border-b border-slate-200 mb-5 sm:mb-6">
          {[
            { id: 'details', label: t('advisorPortal.tabCaseDetails') },
            { id: 'activity', label: t('advisorPortal.tabHistory') },
            {
              id: 'documents',
              label: t('advisorPortal.tabDocuments'),
              title: t('advisorPortal.tabDocumentsTooltip', {
                approved: completedDocCategories,
                total: totalDocCategories,
              }),
            },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              title={tab.title}
              onClick={() => setActiveTab(tab.id)}
              className={`min-h-[44px] shrink-0 px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border-b-2 -mb-px ${activeTab === tab.id ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <SkeletonCaseDetailContent />
        ) : activeTab === 'details' ? (
          <div className="grid gap-6 grid-cols-1 xl:grid-cols-[1fr_38%]">
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl px-4 py-4 sm:px-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide font-medium mb-0.5 text-slate-400">{t('advisorPortal.caseNumber')}</p>
                    <p className="text-sm font-medium text-slate-800 font-mono break-all">{caseId}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide font-medium mb-0.5 text-slate-400">{t('advisorPortal.assignedAdvisor')}</p>
                    <CaseAdvisorPicker
                      userId={userId}
                      sessionId={sessionId}
                      assignedAdvisorId={c.assigned_advisor_id}
                      assignedAdvisorName={c.assigned_advisor_name}
                      onAfterSuccess={refreshCase}
                      onNotify={setToast}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide font-medium mb-0.5 text-slate-400">{t('advisorPortal.caseCreatedAt')}</p>
                    <p className="text-sm font-medium text-slate-800">
                      {formatDisplayDateTime(c.created_at, i18n.language)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wide font-medium mb-0.5 text-slate-400">{t('advisorPortal.caseStatus')}</p>
                    <select
                      value={statusForWorkflow}
                      onChange={(e) => onStatusChange(e.target.value)}
                      disabled={statusSaving}
                      className="w-full text-sm font-medium text-slate-800 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <option value="new">Pending</option>
                      <option value="in_progress">{t('advisorPortal.statusInProgress', { defaultValue: 'In Progress' })}</option>
                      <option value="ready_to_submit">{t('advisorPortal.statusReadyToSubmit', { defaultValue: 'Ready to Submit' })}</option>
                      <option value="submitted">{t('advisorPortal.statusSubmitted', { defaultValue: 'Submitted' })}</option>
                    </select>
                  </div>
                  <div className={`${referralEditing ? 'sm:col-span-2' : 'sm:col-span-1'} min-w-0 border-t border-slate-100 pt-3`}>
                    {referralEditing ? (
                      <div>
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                          {t('advisorPortal.referralCode')}
                        </p>
                        <div className="flex flex-col gap-2 mb-2 min-[420px]:flex-row">
                          <input
                            type="text"
                            value={referralInput}
                            onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                            placeholder={t('advisorPortal.referralPlaceholder')}
                            maxLength={12}
                            className="min-h-[44px] min-w-0 flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:border-emerald-400 bg-white font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => runReferralLookup()}
                            className="min-h-[44px] text-xs font-medium px-3 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                          >
                            {t('advisorPortal.referralLookup')}
                          </button>
                        </div>
                        {referralLookup?.found ? (
                          <p className="text-xs text-emerald-600 mb-2">
                            <i className="ri-check-line text-xs" aria-hidden /> {referralLookup.name}
                          </p>
                        ) : null}
                        {referralLookup?.self ? (
                          <p className="text-xs text-rose-500 mb-2">{t('advisorPortal.referralSelfError')}</p>
                        ) : null}
                        {referralLookup && referralLookup.found === false && !referralLookup.self ? (
                          <p className="text-xs text-rose-500 mb-2">{t('advisorPortal.referralCodeNotFound')}</p>
                        ) : null}
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={
                              referralSaving ||
                              !referralLookup?.found ||
                              referralLookup?.self ||
                              !referralInput.trim()
                            }
                            onClick={() => saveReferralAttachment()}
                            className="min-h-[40px] text-xs font-medium bg-slate-900 text-white px-3 py-2 rounded-lg hover:bg-slate-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {t('advisorPortal.sectionSave')}
                          </button>
                          {c.case_referral_code ? (
                            <button
                              type="button"
                              disabled={referralSaving}
                              onClick={() => clearReferralAttachment()}
                              className="min-h-[40px] text-xs font-medium text-rose-500 px-3 py-2 rounded-lg hover:bg-rose-50 cursor-pointer whitespace-nowrap disabled:opacity-50"
                            >
                              {t('advisorPortal.remove')}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            disabled={referralSaving}
                            onClick={() => {
                              setReferralEditing(false);
                              setReferralInput('');
                              setReferralLookup(null);
                            }}
                            className="min-h-[40px] text-xs text-slate-500 hover:text-slate-700 cursor-pointer whitespace-nowrap"
                          >
                            {t('advisorPortal.sectionCancel')}
                          </button>
                        </div>
                      </div>
                    ) : displayedReferralCode ? (
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{t('advisorPortal.referralCode')}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-0.5">
                            <span className="text-sm font-medium text-slate-800 font-mono">{displayedReferralCode}</span>
                            {displayedReferralName ? (
                              <>
                                <span className="text-sm text-slate-500">—</span>
                                <span className="text-sm text-teal-600 font-medium truncate">{displayedReferralName}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setReferralEditing(true);
                            setReferralInput(String(displayedReferralCode || ''));
                            setReferralLookup(null);
                          }}
                          className="text-slate-400 hover:text-slate-600 cursor-pointer mt-1 shrink-0"
                          aria-label={t('advisorPortal.editReferralAria')}
                        >
                          <i className="ri-pencil-line text-sm" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{t('advisorPortal.referralCode')}</p>
                          <p className="text-sm text-slate-400 mt-0.5">{t('advisorPortal.referralNone')}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setReferralEditing(true);
                            setReferralInput('');
                            setReferralLookup(null);
                          }}
                          className="text-slate-400 hover:text-slate-600 cursor-pointer mt-1 shrink-0"
                          aria-label={t('advisorPortal.addReferralAria')}
                        >
                          <i className="ri-pencil-line text-sm" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="sm:col-start-2 border-t border-slate-100 pt-3">
                    <p className="text-[11px] uppercase tracking-wide font-medium mb-0.5 text-slate-400">{t('advisorPortal.lastUpdated')}</p>
                    <p className="text-sm font-medium text-slate-800">
                      {formatDisplayDateTime(c.updated_at, i18n.language)}
                    </p>
                  </div>
                  <div className="sm:col-span-2 mt-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide font-medium mb-0.5 text-slate-400">{t('advisorPortal.caseType')}</p>
                        <p className="text-sm font-medium text-slate-800">{displayCaseTypeName(c.form_name || c.form_type)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wide font-medium mb-0.5 text-slate-400">PATHWAY</p>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-slate-800 leading-tight break-words">
                            {initialCasePathway === 'asylum'
                              ? 'ASYLUM'
                              : String(applyingForLabel(initialCasePathway) || '')}
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowCaseTypeEditor(true)}
                            className="text-slate-400 hover:text-slate-600 cursor-pointer shrink-0"
                            aria-label={t('advisorPortal.editCaseTypeAria')}
                          >
                            <i className="ri-pencil-line text-sm" />
                          </button>
                        </div>
                      </div>
                    </div>
                    {canMarkSubmitted ? (
                      <div className="mt-3">
                        <button
                          type="button"
                          disabled={statusSaving}
                          onClick={markSubmitted}
                          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 text-sm font-medium bg-slate-700 text-white px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-send-plane-line text-sm" />
                          {statusSaving
                            ? t('advisorPortal.sectionSaving')
                            : t('advisorPortal.markAsSubmitted', { defaultValue: 'Mark as Submitted' })}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <CollapsibleSection
                title={t('advisorPortal.sectionContact')}
                iconClass="ri-contacts-line"
                headerActions={editingSection === 'contact' ? null : <SectionEditButton onClick={() => startEditing('contact')} />}
              >
                {editingSection === 'contact' ? (
                  <div>
                    <div className="mt-4 space-y-4">
                      <div>
                        <p className="text-[11px] uppercase tracking-wide font-medium mb-1.5 block text-slate-500">
                          {t('advisorPortal.fieldMobile')}
                        </p>
                        <MobileFieldWithVerify
                          value={editDraft.mobile || ''}
                          onChange={(local) =>
                            setEditDraft((prev) => ({
                              ...prev,
                              mobile: `${contactMobileCountryCode} ${extractLocalNumber(local || '')}`.trim(),
                            }))
                          }
                          onAfterChange={() => markFieldEdited('mobile')}
                          countryCode={contactMobileCountryCode}
                          onCountryCodeChange={(code) => {
                            setContactMobileCountryCode(code);
                            setEditDraft((prev) => ({
                              ...prev,
                              mobile: `${code} ${extractLocalNumber(String(prev.mobile || ''))}`.trim(),
                            }));
                            markFieldEdited('mobile');
                          }}
                          channel={contactMobileChannel}
                          onChannelChange={(ch) => {
                            setContactMobileChannel(ch);
                            setEditDraft((prev) => ({ ...prev, channel: ch.toLowerCase() }));
                            markFieldEdited('channel');
                          }}
                          verified={contactMobileVerified}
                          onVerifiedChange={setContactMobileVerified}
                          onRequestOtp={requestContactPhoneOtp}
                          onVerifyOtp={verifyContactPhoneOtp}
                          onError={(msg) => showPopupMessage(msg, 'error')}
                          lockInputsWhileVerifying
                          onVerifyingChange={setContactMobileVerifying}
                        />
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide font-medium mb-1.5 block text-slate-500">
                          {t('advisorPortal.fieldEmail')}
                        </p>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-1.5">
                          <input
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            value={editDraft.email || ''}
                            disabled={contactEmailVerifying && !contactEmailVerified}
                            onChange={(e) => {
                              setEditDraft((prev) => ({ ...prev, email: e.target.value }));
                              markFieldEdited('email');
                              if (contactEmailVerified) {
                                setContactEmailVerified(false);
                                setContactEmailVerifying(false);
                                setContactEmailCode('');
                              }
                            }}
                            className="min-h-[44px] min-w-0 flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                          {contactEmailVerified ? (
                            <span className="inline-flex min-h-[40px] items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded-lg whitespace-nowrap shrink-0">
                              <i className="ri-checkbox-circle-fill text-emerald-500 text-xs" />
                              {t('advisorPortal.mobileFieldVerified')}
                            </span>
                          ) : !contactEmailVerifying ? (
                            <button
                              type="button"
                              onClick={async () => {
                                const normalizedEmail = String(editDraft.email || '').trim().toLowerCase();
                                if (!isStrictEmail(normalizedEmail)) return;
                                try {
                                  await mvpApi.adminRequestEmailOtp(normalizedEmail);
                                  setContactEmailVerifying(true);
                                } catch (err) {
                                  const msg = err?.response?.data?.detail || err?.message || 'Failed to send code';
                                  showPopupMessage(typeof msg === 'string' ? msg : 'Failed to send code', 'error');
                                }
                              }}
                              disabled={!isStrictEmail(editDraft.email || '') || (contactMobileVerifying && !contactMobileVerified)}
                              className="min-h-[44px] text-[11px] font-semibold text-slate-600 border border-slate-200 bg-white px-2.5 py-2 rounded-lg hover:border-slate-400 hover:text-slate-800 transition-colors cursor-pointer whitespace-nowrap shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {t('advisorPortal.mobileFieldVerify')}
                            </button>
                          ) : null}
                        </div>
                        {contactEmailVerifying && !contactEmailVerified ? (
                          <div className="flex items-center gap-2 flex-wrap pl-1 mt-2">
                            <input
                              type="text"
                              value={contactEmailCode}
                              onChange={(e) => setContactEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="——————"
                              maxLength={6}
                              className="w-full max-w-xs min-h-[44px] px-3 py-2 text-base font-mono border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 bg-white text-center tracking-[0.35em] sm:w-48"
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                if (!/^\d{6}$/.test(contactEmailCode)) return;
                                const normalizedEmail = String(editDraft.email || '').trim().toLowerCase();
                                try {
                                  await mvpApi.adminVerifyEmailOtp(normalizedEmail, contactEmailCode);
                                  await persistVerifiedContactField('email', normalizedEmail);
                                  setContactEmailVerified(true);
                                  setContactEmailVerifying(false);
                                  setContactEmailCode('');
                                } catch (err) {
                                  const msg = err?.response?.data?.detail || err?.message || 'Failed to verify code';
                                  showPopupMessage(typeof msg === 'string' ? msg : 'Failed to verify code', 'error');
                                }
                              }}
                              disabled={!/^\d{6}$/.test(contactEmailCode)}
                              className="min-h-[40px] text-[11px] font-semibold px-2.5 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setContactEmailVerifying(false);
                                setContactEmailCode('');
                              }}
                              className="min-h-[40px] text-[11px] text-slate-400 hover:text-slate-600 cursor-pointer whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide font-medium mb-1.5 block text-slate-500">
                          {t('advisorPortal.fieldPreferredContact')}
                        </p>
                        <div className="flex items-center gap-0.5 bg-slate-100 rounded-full p-0.5 w-fit">
                          {['Mobile', 'Email'].map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => {
                                setEditDraft((prev) => ({ ...prev, preferredContact: opt }));
                                markFieldEdited('preferredContact');
                              }}
                              className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                                String(editDraft.preferredContact || '').toLowerCase() === opt.toLowerCase()
                                  ? 'bg-white text-slate-800'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 mt-6 pt-4 border-t border-slate-200 min-[420px]:flex-row min-[420px]:items-center min-[420px]:gap-3">
                      <button
                        type="button"
                        onClick={saveSection}
                        disabled={savingSection}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="ri-save-line text-sm" />
                        {savingSection ? t('advisorPortal.sectionSaving') : t('advisorPortal.sectionSave')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingSection(null); setEditDraft({}); setInitialEditDraft({}); setEditedFields(new Set()); setFieldErrors({}); }}
                        className="min-h-[44px] text-sm font-medium px-4 py-2 rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {t('advisorPortal.sectionCancel')}
                      </button>
                    </div>
                  </div>
                ) : <DetailsGrid items={contactPairs} />}
              </CollapsibleSection>
              <CollapsibleSection
                title={t('advisorPortal.sectionPersonalInformation')}
                iconClass="ri-id-card-line"
                headerActions={
                  editingSection === 'identity'
                    ? null
                    : <SectionEditButton onClick={() => startEditing('identity')} />
                }
              >
                {editingSection === 'identity' ? (
                  (() => {
                    const normalizedDocType = String(editDraft.docType || 'passport').trim().toLowerCase();
                    const identityDocNumberFieldKey =
                      normalizedDocType === 'passport'
                        ? 'passportNumber'
                        : normalizedDocType === 'national_id'
                        ? 'nationalIdNumber'
                        : normalizedDocType === 'travel_document'
                        ? 'travelDocumentNumber'
                        : normalizedDocType === 'cedula_inscripcion'
                        ? 'registrationDocumentNumber'
                        : 'otherDocumentNumber';
                    return (
                  <EditableGrid
                    fields={[
                      ['firstName', t('advisorPortal.fieldFirstName')],
                      ['lastName', t('advisorPortal.fieldLastName')],
                      ['secondSurname', t('advisorPortal.fieldSecondSurname')],
                      ['dob', t('advisorPortal.fieldDateOfBirth'), 'date'],
                      ['placeOfBirth', t('advisorPortal.fieldPlaceOfBirth')],
                      ['countryOfBirth', t('advisorPortal.fieldCountryOfBirth')],
                      [
                        'docType',
                        t('section6.nationalIdType', { defaultValue: 'Document type' }),
                        'select',
                        [
                          { value: 'passport', label: t('section6.docTypeOptions.passport', { defaultValue: 'Passport' }) },
                          { value: 'national_id', label: t('section6.docTypeOptions.nationalId', { defaultValue: 'National ID card' }) },
                          { value: 'travel_document', label: t('section6.docTypeOptions.travelDoc', { defaultValue: 'Travel document' }) },
                          { value: 'cedula_inscripcion', label: t('section6.docTypeOptions.cedulaInscripcion', { defaultValue: 'Registration certificate' }) },
                          { value: 'other', label: t('section6.docTypeOptions.other', { defaultValue: 'Other' }) },
                        ],
                      ],
                      [identityDocNumberFieldKey, t('section6.nationalIdNumber', { defaultValue: 'Document number' })],
                      ['nationality', t('advisorPortal.fieldNationality')],
                      ['sex', t('advisorPortal.fieldSex'), 'select', APPLICANT_SEX_OPTIONS],
                      [
                        'civilStatus',
                        t('advisorPortal.fieldCivilStatus'),
                        'select',
                        [
                          { value: 'single', label: t('advisorPortal.civilStatusSingle', { defaultValue: 'Single' }) },
                          { value: 'married', label: t('advisorPortal.civilStatusMarried', { defaultValue: 'Married' }) },
                          { value: 'widowed', label: t('advisorPortal.civilStatusWidowed', { defaultValue: 'Widowed' }) },
                          { value: 'divorced', label: t('advisorPortal.civilStatusDivorced', { defaultValue: 'Divorced' }) },
                          { value: 'separated', label: t('advisorPortal.civilStatusSeparated', { defaultValue: 'Separated' }) },
                        ],
                      ],
                      ['nie', t('advisorPortal.fieldNie'), 'nie'],
                      ['fatherName', t('advisorPortal.familyFather')],
                      ['motherName', t('advisorPortal.familyMother')],
                    ]}
                    draft={editDraft}
                    onChange={setEditDraft}
                    onFieldEdited={markFieldEdited}
                    errors={fieldErrors}
                    onCancel={() => { setEditingSection(null); setEditDraft({}); setInitialEditDraft({}); setEditedFields(new Set()); setFieldErrors({}); }}
                    onSave={saveSection}
                    saving={savingSection}
                  />
                    );
                  })()
                ) : (
                  <>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">{t('advisorPortal.subsectionIdentity')}</p>
                        </div>
                        <DetailsGrid items={personalIdentityPairs} />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">{t('advisorPortal.subsectionOther')}</p>
                        </div>
                        <DetailsGrid items={personalOtherPairs} />
                      </div>
                    </div>
                  </>
                )}
              </CollapsibleSection>
              <CollapsibleSection
                title={t('advisorPortal.sectionAddress')}
                iconClass="ri-map-pin-line"
                headerActions={editingSection === 'address' ? null : <SectionEditButton onClick={() => startEditing('address')} />}
              >
                {editingSection === 'address' ? (
                  <EditableGrid
                    fields={[
                      ['street', t('advisorPortal.fieldStreetAddress')],
                      ['number', t('advisorPortal.fieldNumber')],
                      ['floor', t('advisorPortal.fieldFloor'), 'select', SPAIN_ADDRESS_FLOOR_OPTIONS],
                      ['door', t('advisorPortal.fieldDoor')],
                      ['city', t('advisorPortal.fieldCity')],
                      ['province', t('advisorPortal.fieldProvince'), 'select', SPAIN_PROVINCE_OPTIONS],
                      ['postalCode', t('advisorPortal.fieldPostalCode'), 'postal'],
                    ]}
                    draft={editDraft}
                    onChange={setEditDraft}
                    onFieldEdited={markFieldEdited}
                    errors={fieldErrors}
                    onCancel={() => { setEditingSection(null); setEditDraft({}); setInitialEditDraft({}); setEditedFields(new Set()); setFieldErrors({}); }}
                    onSave={saveSection}
                    saving={savingSection}
                  />
                ) : <DetailsGrid items={addressPairs} />}
              </CollapsibleSection>
              <CollapsibleSection
                title={t('advisorPortal.sectionRepresentative')}
                iconClass="ri-briefcase-line"
                defaultOpen={false}
                headerActions={editingSection === 'representative' ? null : <SectionEditButton onClick={() => startEditing('representative')} />}
              >
                {editingSection === 'representative' ? (
                  <EditableGrid
                    fields={[
                      ['companyName', t('advisorPortal.fieldFirmName', { defaultValue: 'Firm Name' })],
                      ['documentId', t('advisorPortal.fieldDniNieCif', { defaultValue: 'DNI/NIE/CIF' })],
                      ['street', t('advisorPortal.fieldAddress', { defaultValue: 'Address' })],
                      ['number', t('advisorPortal.fieldNumber')],
                      ['floor', t('advisorPortal.fieldFloor'), 'select', SPAIN_ADDRESS_FLOOR_OPTIONS],
                      ['city', t('advisorPortal.fieldLocality', { defaultValue: 'Locality' })],
                      ['postalCode', t('advisorPortal.fieldPostalCode'), 'postal'],
                      ['province', t('advisorPortal.fieldProvince'), 'select', SPAIN_PROVINCE_OPTIONS],
                      ['phone', t('advisorPortal.fieldMobile', { defaultValue: 'Mobile' }), 'tel'],
                      ['email', t('advisorPortal.fieldEmail'), 'email'],
                    ]}
                    draft={editDraft}
                    onChange={setEditDraft}
                    onFieldEdited={markFieldEdited}
                    errors={fieldErrors}
                    onCancel={() => { setEditingSection(null); setEditDraft({}); setInitialEditDraft({}); setEditedFields(new Set()); setFieldErrors({}); }}
                    onSave={saveSection}
                    saving={savingSection}
                  />
                ) : <DetailsGrid items={representativePairs} />}
              </CollapsibleSection>
              <CollapsibleSection
                title={t('advisorPortal.sectionNotificationAddress')}
                iconClass="ri-mail-send-line"
                defaultOpen={false}
                headerActions={editingSection === 'notification' ? null : <SectionEditButton onClick={() => startEditing('notification')} />}
              >
                {editingSection === 'notification' ? (
                  <EditableGrid
                    fields={[
                      ['name', t('advisorPortal.fieldName')],
                      ['documentId', t('advisorPortal.fieldDocumentId')],
                      ['street', t('advisorPortal.fieldStreet')],
                      ['number', t('advisorPortal.fieldNumber')],
                      ['floor', t('advisorPortal.fieldFloor'), 'select', SPAIN_ADDRESS_FLOOR_OPTIONS],
                      ['city', t('advisorPortal.fieldCity')],
                      ['postalCode', t('advisorPortal.fieldPostalCode'), 'postal'],
                      ['province', t('advisorPortal.fieldProvince'), 'select', SPAIN_PROVINCE_OPTIONS],
                      ['phone', t('advisorPortal.fieldPhone'), 'tel'],
                      ['email', t('advisorPortal.fieldEmail'), 'email'],
                      ['electronic', t('advisorPortal.fieldElectronicNotifications'), 'select', ['Sí', 'No', 'Yes', 'No']],
                    ]}
                    draft={editDraft}
                    onChange={setEditDraft}
                    onFieldEdited={markFieldEdited}
                    errors={fieldErrors}
                    onCancel={() => { setEditingSection(null); setEditDraft({}); setInitialEditDraft({}); setEditedFields(new Set()); setFieldErrors({}); }}
                    onSave={saveSection}
                    saving={savingSection}
                  />
                ) : <DetailsGrid items={notificationPairs} />}
              </CollapsibleSection>
            </div>
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
                  <div className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
                  <h2 className="text-sm font-semibold text-slate-800">{t('advisorPortal.addNoteTitle')}</h2>
                </div>
                <div className="px-4 py-3">
                  <ActivityInputBar
                    variant="inline"
                    requiredDocuments={mergedDocSlots}
                    advisorName={advisorName}
                    caseId={caseId}
                    posting={notePosting}
                    onPost={handlePostHistoryNote}
                    hideTypeSelector
                  />
                </div>
              </div>
                <ServiceFeeCard
                  userId={userId}
                  sessionId={sessionId}
                  serviceFeeEur={c.service_fee_eur}
                  feeCampaignCode={c.fee_campaign_code}
                  feeCampaignLabel={c.fee_campaign_label}
                  feeCampaignDefaultEur={c.fee_campaign_default_eur}
                  applicantReferralToken={c.applicant_referral_token}
                  onAfterSave={afterCaseExtensionSave}
                  onToast={setToast}
                />
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
                  <h3 className="text-sm font-semibold text-slate-800 flex-1">{t('advisorPortal.recentHistory')}</h3>
                  <button type="button" onClick={() => setActiveTab('activity')} className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap">
                    {t('advisorPortal.viewAll')}
                  </button>
                </div>
                <div className="space-y-3">
                  {!recentActivity.length ? (
                    <p className="text-xs text-slate-400">{t('advisorPortal.noHistoryYet')}</p>
                  ) : recentActivity.map((e) => {
                    const isLq =
                      e.kind === 'lead_qualification' || String(e.id || '').startsWith('lead-qualification:');
                    const vis = caseHistoryKindVisual(isLq ? 'lead_qualification' : e.kind);
                    return (
                      <div key={e.id} className="border-b border-slate-100 pb-2.5 last:border-0">
                        <div className="flex gap-2.5">
                          <div
                            className={`w-7 h-7 shrink-0 rounded-md flex items-center justify-center ${vis.box}`}
                          >
                            <i className={`${vis.icon} text-sm leading-none`} aria-hidden />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs font-semibold text-slate-800 truncate min-w-0">{e.title}</p>
                              <p className="text-[10px] text-slate-400 whitespace-nowrap shrink-0">
                                {formatCaseHistoryAt(e.at, i18n.language)}
                              </p>
                            </div>
                            {e.kind === 'applicant_help_message' && e.body ? (
                              <button
                                type="button"
                                onClick={() => { setHelpMessageModal(e); setHelpReplyText(''); }}
                                className="text-left text-[11px] text-slate-600 mt-0.5 line-clamp-2 hover:text-slate-800 cursor-pointer transition-colors"
                              >
                                &ldquo;{e.body}&rdquo;
                                {!e.meta?.replied && (
                                  <span className="ml-1 text-amber-600 font-medium">&middot; {t('advisorPortal.helpPending')}</span>
                                )}
                              </button>
                            ) : e.kind === 'applicant_family_application_interest' && e.body ? (
                              <button
                                type="button"
                                onClick={() => { setHelpMessageModal(e); setHelpReplyText(''); }}
                                className="text-left text-[11px] text-slate-600 mt-0.5 line-clamp-2 hover:text-slate-800 cursor-pointer transition-colors"
                              >
                                &ldquo;{e.body}&rdquo;
                                {!e.meta?.replied && !e.meta?.dismissed && (
                                  <span className="ml-1 text-amber-600 font-medium">&middot; {t('advisorPortal.helpPending')}</span>
                                )}
                              </button>
                            ) : e.body ? (
                              <p className="text-[11px] text-slate-500 mt-0.5 truncate">{e.body}</p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
                  <h3 className="text-sm font-semibold text-slate-800 flex-1">{t('advisorPortal.documentsCardTitle')}</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setHighlightDocId(null);
                      setActiveTab('documents');
                    }}
                    className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                  >
                    {t('advisorPortal.manage')}
                  </button>
                </div>
                {(() => {
                  const normalizePathway = (value) => {
                    const raw = String(value || '').trim().toLowerCase();
                    if (!raw) return '';
                    if (raw === '7a' || raw === 'work') return 'work';
                    if (raw === '7b' || raw === 'family') return 'family';
                    if (raw === '7c' || raw === 'vulnerability') return 'vulnerability';
                    return '';
                  };
                  const collectedPick = (...keys) => {
                    for (const key of keys) {
                      const value = collected?.[key];
                      if (value == null) continue;
                      const s = String(value).trim();
                      if (s) return s;
                    }
                    return '';
                  };
                  const asylumRaw =
                    c.asylum_applicant ??
                    collected?.asylum_applicant ??
                    collected?.solicitante_asilo ??
                    collected?.asylum_routing_confirmed;
                  const isAsylumApplicant =
                    asylumRaw === true ||
                    String(asylumRaw || '').trim().toLowerCase() === 'yes' ||
                    String(asylumRaw || '').trim().toLowerCase() === 'true' ||
                    String(asylumRaw || '').trim() === '1';
                  const resolvedPathway =
                    normalizePathway(c.sub_route) ||
                    normalizePathway(c.irregular_sub_route) ||
                    normalizePathway(c.qualifying_ground) ||
                    normalizePathway(collectedPick('sub_route', 'irregular_sub_route', 'qualifying_ground', 'ruta_irregular'));
                  const normalized = (documents || []).map((d) => {
                    const section = String(d?.section_id || d?.document_type || '').trim().toLowerCase();
                    const docType = String(d?.document_type || '').trim().toLowerCase();
                    const desc = String(d?.supporting_description || '').trim().toLowerCase();
                    return { section, docType, desc };
                  });
                  const countWhere = (fn) => normalized.filter(fn).length;
                  const vulnerabilityCount = countWhere(
                    (d) =>
                      d.docType === 'vulnerability_report' ||
                      (d.section === 'other_supporting' && d.desc === 'vulnerability_report'),
                  );
                  const asylumCount = countWhere(
                    (d) =>
                      d.section === 'asylum_section' ||
                      d.docType === 'red_card_id' ||
                      d.docType === 'application_receipt_id' ||
                      d.docType === 'denial_resolution_id',
                  );
                  const employmentCount = countWhere(
                    (d) =>
                      d.section === 'employment_section' ||
                      d.docType === 'work_contract_id' ||
                      d.docType === 'nomina_id' ||
                      d.docType === 'vida_laboral_id' ||
                      d.docType === 'job_offer_letter_id' ||
                      d.docType === 'other_documents_id' ||
                      d.docType === 'self_employment_id',
                  );
                  const familyCount = countWhere(
                    (d) =>
                      d.section === 'family_section' ||
                      d.docType === 'marriage_certificate_id' ||
                      d.docType === 'libro_de_familia_id' ||
                      d.docType === 'childrens_birth_certificates_id' ||
                      d.docType === 'proof_of_cohabitation_id' ||
                      d.docType === 'school_enrollment_id' ||
                      d.docType === 'other_documents_id',
                  );
                  const otherCount = countWhere(
                    (d) =>
                      (d.section === 'other_supporting' || d.section === 'misc') &&
                      d.docType !== 'vulnerability_report' &&
                      d.desc !== 'vulnerability_report',
                  );
                  const categories = [
                    {
                      id: 'passport',
                      label: t('advisorPortal.sectionIdentity'),
                      uploadedCount: countWhere(
                        (d) => d.section === 'identity_section',
                      ),
                    },
                    {
                      id: 'proof_of_residency',
                      label: t('advisorPortal.docCat_proof_of_residency_name'),
                      uploadedCount: countWhere(
                        (d) => d.section === 'proof_of_residence_section',
                      ),
                    },
                    {
                      id: 'criminal_record',
                      label: t('advisorPortal.docCat_criminal_record_name'),
                      note: t('advisorPortal.historyCriminalRecordNote'),
                      uploadedCount: countWhere(
                        (d) =>
                          d.section === 'criminal_record_certificate' ||
                          d.section === 'nie_cert' ||
                          d.docType.includes('criminal'),
                      ),
                    },
                    ...(isAsylumApplicant
                      ? [{ id: 'asylum_receipt', label: t('advisorPortal.historyDocAsylumDocuments'), uploadedCount: asylumCount }]
                      : []),
                    ...(!isAsylumApplicant && (resolvedPathway === 'work' || employmentCount > 0)
                      ? [{ id: 'employment', label: t('advisorPortal.historyEmployment'), uploadedCount: employmentCount }]
                      : []),
                    ...(!isAsylumApplicant && (resolvedPathway === 'family' || familyCount > 0)
                      ? [{ id: 'family', label: t('advisorPortal.sectionFamily'), uploadedCount: familyCount }]
                      : []),
                    ...(!isAsylumApplicant && (resolvedPathway === 'vulnerability' || vulnerabilityCount > 0)
                      ? [{ id: 'vulnerability', label: t('advisorPortal.historyVulnerabilityReport'), uploadedCount: vulnerabilityCount }]
                      : []),
                    { id: 'other', label: t('advisorPortal.docCat_other_name'), uploadedCount: otherCount },
                  ];
                  return (
                    <div className="space-y-1">
                      {categories.map((cat) => {
                        const doc = mergedDocSlots.find((d) => d.id === cat.id);
                        const side = doc ? categorySidebarStatus(doc) : { text: t('advisorPortal.reviewPending'), variant: 'pending' };
                        const isPending = side.variant === 'pending' || side.variant === 'none' || side.variant === 'awaiting';
                        const uploadedCount = Number(cat.uploadedCount || 0);
                        const statusText = uploadedCount > 0 ? t('advisorPortal.sidebarUploadedN', { count: uploadedCount }) : (isPending ? t('advisorPortal.reviewPending') : side.text);
                        const hasUploads = Boolean(doc?.files?.length) || Boolean(doc?.uploaded);
                        const rowIconClass = categorySidebarIconClass(side, uploadedCount);
                        return (
                          <div key={cat.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveTab('documents');
                                setHighlightDocId(['passport', 'proof_of_residency', 'criminal_record', 'asylum_receipt', 'other'].includes(cat.id) ? cat.id : null);
                              }}
                              className="w-full flex items-center gap-2.5 px-2 py-1.5 text-left hover:bg-slate-50 rounded-md transition-colors cursor-pointer"
                            >
                              {rowIconClass ? <i className={rowIconClass} aria-hidden /> : null}
                              <span className="text-xs font-semibold text-slate-700 flex-1">{cat.label}</span>
                              <span className={`text-[10px] shrink-0 ${statusText === t('advisorPortal.reviewPending') ? 'text-slate-400' : 'text-slate-500'}`}>
                                {statusText}
                              </span>
                            </button>
                            {cat.note && !hasUploads && statusText === t('advisorPortal.reviewPending') ? (
                              <p
                                className={`text-[10px] text-slate-400 pb-1 ${rowIconClass ? 'pl-7' : 'pl-2'}`}
                              >
                                {cat.note}
                              </p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : null}

        {!loading && activeTab === 'activity' ? (
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <ActivityInputBar
                variant="stacked"
                requiredDocuments={mergedDocSlots}
                advisorName={advisorName}
                caseId={caseId}
                posting={notePosting}
                onPost={handlePostHistoryNote}
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5">
                <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center">
                <div className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
                <h3 className="text-sm font-semibold text-slate-800 flex-1">{t('advisorPortal.tabHistory')}</h3>
                <div className="flex w-full min-w-0 items-center gap-1 overflow-x-auto bg-slate-100 rounded-lg p-1 sm:w-auto">
                  {(['all', 'notes', 'activity']).map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => {
                        setHistoryFilter(f);
                        loadHistoryTab(f);
                      }}
                      className={`min-h-[36px] shrink-0 px-3 py-1 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                        historyFilter === f
                          ? 'bg-white text-slate-800 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {f === 'all' ? t('advisorPortal.activityFilterAll') : f === 'notes' ? t('advisorPortal.activityFilterNotes') : t('advisorPortal.activityFilterActivity')}
                    </button>
                  ))}
                </div>
              </div>
              {historyLoading ? (
                <p className="text-sm text-slate-500">{t('advisorPortal.historyLoading')}</p>
              ) : !historyDisplayList.length ? (
                <p className="text-sm text-slate-400 text-center py-8">{t('advisorPortal.historyNoEntriesMatch')}</p>
              ) : (
                <div className="space-y-1">
                  {historyDisplayList.map((e, i) => {
                    const isLast = i === historyDisplayList.length - 1;
                    const isLeadQualification =
                      e.kind === 'lead_qualification' || String(e.id || '').startsWith('lead-qualification:');
                    const vis = caseHistoryKindVisual(
                      isLeadQualification ? 'lead_qualification' : e.kind
                    );
                    return (
                    <div key={e.id} className="flex gap-3">
                      <div className="flex flex-col items-center shrink-0">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${vis.box}`}>
                          <i className={`${vis.icon} text-xs leading-none`} aria-hidden />
                        </div>
                        {!isLast ? <div className="w-px flex-1 bg-slate-100 mt-1 min-h-[16px]" /> : null}
                      </div>
                      <div className="pb-4 flex-1 min-w-0">
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2 sm:justify-between">
                          <span className="text-sm font-semibold text-slate-700 leading-tight break-words">{e.title}</span>
                          <span className="text-[11px] text-slate-400 whitespace-nowrap shrink-0 sm:ml-2">
                            {formatCaseHistoryAt(e.at, i18n.language)}
                          </span>
                        </div>
                        {e.kind === 'applicant_help_message' && e.body ? (
                          <div className="mt-1">
                            <button
                              type="button"
                              onClick={() => { setHelpMessageModal(e); setHelpReplyText(''); }}
                              className="w-full text-left bg-amber-50 border border-amber-100 hover:border-amber-200 rounded-md px-2.5 py-1.5 cursor-pointer transition-colors"
                            >
                              <p className="text-xs text-slate-600 whitespace-pre-line line-clamp-2">
                                &ldquo;{e.body}&rdquo;
                              </p>
                            </button>
                            <div className="flex items-center gap-2 mt-1">
                              {e.actor_name && (
                                <span className="text-[11px] text-slate-400">— {e.actor_name}</span>
                              )}
                              {e.meta?.replied ? (
                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                                  <i className="ri-check-double-line text-xs" /> {t('advisorPortal.helpResponded')}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 font-medium">
                                  <i className="ri-time-line text-xs" /> {t('advisorPortal.helpPending')}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : e.kind === 'applicant_family_application_interest' && e.body ? (
                          <div className="mt-1">
                            <button
                              type="button"
                              onClick={() => { setHelpMessageModal(e); setHelpReplyText(''); }}
                              className="w-full text-left bg-amber-50 border border-amber-100 hover:border-amber-200 rounded-md px-2.5 py-1.5 cursor-pointer transition-colors"
                            >
                              <p className="text-xs text-slate-600 whitespace-pre-line line-clamp-2">
                                &ldquo;{e.body}&rdquo;
                              </p>
                            </button>
                            <div className="flex items-center gap-2 mt-1">
                              {e.actor_name && (
                                <span className="text-[11px] text-slate-400">— {e.actor_name}</span>
                              )}
                              {e.meta?.replied || e.meta?.dismissed ? (
                                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                                  <i className="ri-check-double-line text-xs" /> {t('advisorPortal.helpResponded')}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 font-medium">
                                  <i className="ri-time-line text-xs" /> {t('advisorPortal.helpPending')}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (e.kind === 'help_message_replied' || e.kind === 'family_application_interest_replied') && e.body ? (
                          <div className="mt-1">
                            {e.meta?.has_reply ? (
                              <p className="text-xs text-slate-600 bg-emerald-50 border border-emerald-100 rounded-md px-2.5 py-1.5 whitespace-pre-line line-clamp-3">
                                {e.body}
                              </p>
                            ) : (
                              <p className="text-xs text-slate-500 truncate">{e.body}</p>
                            )}
                            {e.meta?.original_message_preview && (
                              <p className="text-[11px] text-slate-400 mt-1 truncate">
                                Re: &ldquo;{e.meta.original_message_preview}&rdquo;
                              </p>
                            )}
                            {e.actor_name && (
                              <span className="text-[11px] text-slate-400 mt-0.5 inline-block">— {e.actor_name}</span>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {[e.body, e.document_tag].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {!loading && activeTab === 'documents' ? (
          <DocumentsTab
            userId={userId}
            sessionId={sessionId}
            publicCaseId={c.public_case_id}
            documents={documents}
            archivedDocuments={archivedDocuments}
            collectedFields={collected}
            asylumApplicant={c.asylum_applicant}
            priorAsylumApplication={c.prior_asylum_application}
            qualifyingGround={c.qualifying_ground || c.irregular_sub_route || ''}
            subRoute={c.sub_route || c.irregular_sub_route || ''}
            highlightDocId={highlightDocId}
            onClearHighlight={() => setHighlightDocId(null)}
            onRefresh={async () => {
              await refreshCase();
              await loadHistoryTab(historyFilter);
              if (historyFilter !== 'all') {
                await refreshHistoryPreview();
              }
            }}
          />
        ) : null}
        {confirmHideCustomerBannerOpen ? (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center px-3 py-3 sm:items-center sm:px-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
            onClick={() => {
              if (confirmHideCustomerBannerSaving) return;
              setConfirmHideCustomerBannerOpen(false);
            }}
          >
            <div
              className="w-full max-w-md max-h-[calc(100dvh-1.5rem)] overflow-y-auto bg-white rounded-2xl shadow-xl"
              onClick={(ev) => ev.stopPropagation()}
            >
              <div className="px-5 pt-5 pb-3">
                <h3 className="text-sm font-semibold text-slate-900">
                  Hide customer message banner?
                </h3>
              </div>
              <div className="px-5 pb-4">
                <p className="text-[13px] text-slate-600 leading-relaxed">
                  Do you wish to turn off seeing this message for this case?
                </p>
              </div>
              <div className="border-t border-slate-100 px-5 py-4 flex flex-col-reverse gap-2.5 min-[420px]:flex-row min-[420px]:items-center">
                <button
                  type="button"
                  disabled={confirmHideCustomerBannerSaving}
                  onClick={() => setConfirmHideCustomerBannerOpen(false)}
                  className="min-h-[44px] w-full min-[420px]:w-auto px-4 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-600 text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={confirmHideCustomerBannerSaving}
                  onClick={() => { void handlePersistHideCustomerBanner(); }}
                  className="min-h-[44px] w-full min-[420px]:ml-auto min-[420px]:w-auto px-4 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {confirmHideCustomerBannerSaving ? 'Saving...' : 'Turn off'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {helpMessageModal ? (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center px-3 py-3 sm:items-center sm:px-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
            onClick={() => setHelpMessageModal(null)}
          >
            <div
              className="w-full max-w-md max-h-[calc(100dvh-1.5rem)] overflow-y-auto bg-white rounded-2xl shadow-xl"
              onClick={(ev) => ev.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <i
                      className={
                        helpMessageModal.kind === 'applicant_family_application_interest'
                          ? 'ri-group-line text-amber-600 text-sm'
                          : 'ri-question-line text-amber-600 text-sm'
                      }
                    />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    {helpMessageModal.kind === 'applicant_family_application_interest'
                      ? t('advisorPortal.familyInterestModalTitle')
                      : t('advisorPortal.helpModalTitle')}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setHelpMessageModal(null)}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="px-5 pb-4">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400 mb-2">
                  {helpMessageModal.actor_name && (
                    <span>{helpMessageModal.actor_name}</span>
                  )}
                  {helpMessageModal.at && (
                    <span>· {formatCaseHistoryAt(helpMessageModal.at, i18n.language)}</span>
                  )}
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-3">
                  <p className="text-[13px] text-slate-700 whitespace-pre-line leading-relaxed">
                    {helpMessageModal.body}
                  </p>
                </div>
                {helpMessageModal.meta?.section && (
                  <p className="text-[11px] text-slate-400 mt-2">
                    {t('advisorPortal.helpModalSection')}: {helpMessageModal.meta.section.replace(/_/g, ' ')}
                  </p>
                )}
              </div>

              {helpMessageModal.meta?.replied ? (
                <div className="border-t border-slate-100 px-5 py-4 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
                    <i className="ri-check-double-line" /> {t('advisorPortal.helpResponded')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setHelpMessageModal(null)}
                    className="ml-auto px-4 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-600 text-sm font-medium cursor-pointer transition-colors"
                  >
                    {t('advisorPortal.helpModalClose')}
                  </button>
                </div>
              ) : (
                <div className="border-t border-slate-100 px-5 py-4">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    {t('advisorPortal.helpReplyLabel')}
                  </label>
                  <textarea
                    value={helpReplyText}
                    onChange={(ev) => setHelpReplyText(ev.target.value)}
                    placeholder={t('advisorPortal.helpReplyPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 resize-none bg-white"
                  />
                  <div className="flex flex-col gap-2.5 mt-3 min-[420px]:flex-row min-[420px]:items-center">
                    <button
                      type="button"
                      disabled={!helpReplyText.trim() || helpResponding}
                      onClick={() => handleMarkHelpResponded(helpMessageModal.id)}
                      className="min-h-[44px] flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-medium cursor-pointer disabled:cursor-not-allowed transition-colors"
                    >
                      {helpResponding ? t('advisorPortal.helpRespondingBtn') : t('advisorPortal.helpReplyAndResolve')}
                    </button>
                    <button
                      type="button"
                      disabled={helpResponding}
                      onClick={() => handleMarkHelpResponded(helpMessageModal.id)}
                      className="min-h-[44px] px-4 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 text-slate-600 text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('advisorPortal.helpMarkRespondedBtn')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {toast ? (
          <div className="fixed bottom-4 left-1/2 z-50 max-w-[calc(100vw-1.5rem)] -translate-x-1/2 rounded-xl bg-slate-900 px-4 py-2.5 text-xs font-medium text-white sm:bottom-6">
            {toast}
          </div>
        ) : null}
        <GenerateFormModal
          open={generateFormOpen}
          onClose={() => setGenerateFormOpen(false)}
          userId={userId}
          sessionId={sessionId}
          isDraft={ex10PdfIsDraft}
          caseDisplayId={caseId}
          applicantName={c.user_name || ''}
          formCode={advisorFormCode}
          firstName={read('nombre', 'first_name') === '—' ? '' : read('nombre', 'first_name')}
          lastName={read('apellidos', 'last_name') === '—' ? '' : read('apellidos', 'last_name')}
          secondSurname={read('segundo_apellido', 'second_surname') === '—' ? '' : read('segundo_apellido', 'second_surname')}
          identityPairs={identityPairs}
          contactPairs={contactPairs}
          addressPairs={addressPairs}
          representativePairs={representativePairs}
          notificationPairs={notificationPairs}
        />
        <DownloadPackageModal
          open={downloadPackageOpen}
          onClose={() => setDownloadPackageOpen(false)}
          onDownloadSuccess={(name) =>
            setToast(t('advisorPortal.downloadStarted', { name: name || t('advisorPortal.packageNameFallback') }))
          }
          userId={userId}
          sessionId={sessionId}
          caseDisplayId={caseId}
          applicantName={c.user_name || ''}
          merged={mergedDocSlots}
          ex10PdfIsDraft={ex10PdfIsDraft}
          identityPairs={identityPairs}
          contactPairs={contactPairs}
          addressPairs={addressPairs}
          representativePairs={representativePairs}
          notificationPairs={notificationPairs}
        />
      </main>
    </div>
  );
}

function DetailsGrid({ items }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
      {items.map(([label, value], idx) => {
        const valueMeta =
          value && typeof value === 'object' && !React.isValidElement(value) && Object.prototype.hasOwnProperty.call(value, 'value')
            ? value
            : null;
        const rawValue = valueMeta ? valueMeta.value : value;
        const showVerifiedTick = Boolean(valueMeta?.verified);
        const isElectronic = label === t('advisorPortal.fieldElectronicNotifications');
        const normalizedValue = String(rawValue || '').trim().toLowerCase();
        const valueText = String(rawValue || '').trim();
        const displayValue = (valueText === '—' || valueText === 'â€”') ? '' : rawValue;
        const electronicIsYes = ['yes', 'yes, consented', 'sí', 'si', 'si, consentido', 'sí, consentido', 'true', '1'].includes(normalizedValue);
        const electronicIsNo = ['no', 'false', '0'].includes(normalizedValue);
        const body =
          isElectronic && electronicIsYes ? (
            <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
              {value || t('advisorPortal.qualificationAnswerYes')}
              <i
                className="ri-checkbox-circle-fill text-emerald-600 text-lg leading-none shrink-0"
                aria-hidden
              />
            </span>
          ) : isElectronic && electronicIsNo ? (
            <span className="text-sm text-slate-800">{t('advisorPortal.qualificationAnswerNo')}</span>
          ) : showVerifiedTick ? (
            <span className="inline-flex items-center gap-2 text-sm text-slate-800">
              <span>{displayValue || ''}</span>
              <i
                className="ri-checkbox-circle-fill text-emerald-600 text-base leading-none shrink-0"
                aria-label={t('advisorPortal.mobileFieldVerified')}
              />
            </span>
          ) : (
            displayValue || ''
          );
        return (
          <div key={`${label}-${idx}`} className="min-w-0">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{label}</p>
            <div className="min-w-0 break-words text-sm text-slate-800">{body}</div>
          </div>
        );
      })}
    </div>
  );
}

function GroupedDetailsGrid({ groups }) {
  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.title}>
          <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-3">{group.title}</p>
          <DetailsGrid items={group.items} />
        </div>
      ))}
    </div>
  );
}

function SectionEditButton({ onClick, compact = false }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center text-xs text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
    >
      <i className="ri-pencil-line mr-1" />
      {t('advisorPortal.sectionEdit')}
    </button>
  );
}

function SearchableSelect({ value, onChange, options, className, placeholder, onFieldEdited }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const entries = useMemo(() => {
    return options.map((opt) => {
      if (opt && typeof opt === 'object' && 'value' in opt) return opt;
      return { value: opt, label: opt };
    });
  }, [options]);

  const selected = entries.find((e) => e.value === value);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return entries;
    return entries.filter(
      (e) =>
        String(e.label || '').toLowerCase().includes(term) ||
        String(e.value || '').toLowerCase().includes(term)
    );
  }, [searchTerm, entries]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          setSearchTerm('');
        }}
        className={`${className} flex items-center justify-between text-left`}
      >
        <span className={selected ? 'text-slate-900' : 'text-slate-400'}>
          {selected ? selected.label : placeholder || 'Select...'}
        </span>
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-slate-400`} />
      </button>

      {open && (
        <div className="absolute z-[100] top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden min-w-[160px]">
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <i className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]" />
              <input
                type="text"
                autoFocus
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-slate-400 text-center">No results</div>
            ) : (
              filtered.map((e) => (
                <button
                  key={e.value}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${
                    e.value === value ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-700'
                  }`}
                  onClick={() => {
                    onChange(e.value);
                    onFieldEdited?.();
                    setOpen(false);
                  }}
                >
                  {e.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EditableGrid({ fields, draft, onChange, onFieldEdited, errors = {}, onCancel, onSave, saving }) {
  const { t } = useTranslation();
  const maxBirthIso = useMemo(() => maxBirthDateIsoForAdult(), []);
  const setField = (key, val) => {
    const normalizedValue = (key === 'firstName' || key === 'lastName') ? capitalizeWords(val) : val;
    onChange((prev) => ({ ...prev, [key]: normalizedValue }));
  };

  const inputClass =
    'w-full min-h-[44px] text-sm border border-slate-200 rounded-lg px-2.5 py-2 sm:py-1.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400 bg-white';

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {fields.map((field) => {
          const [key, label, type, options] = field;
          const isSelect = type === 'select' && Array.isArray(options);
          const inputKind = isSelect ? null : type;
          const hasError = Boolean(errors[key]);
          const fieldClass = hasError
            ? `${inputClass} border-rose-400 focus:ring-rose-200/60 focus:border-rose-400`
            : inputClass;

          return (
            <div key={key} className="min-w-0">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{label}</p>
              {isSelect ? (
                key === 'province' ? (
                  <SearchableSelect
                    value={draft[key] || ''}
                    options={options}
                    onChange={(val) => {
                      setField(key, val);
                    }}
                    onFieldEdited={() => onFieldEdited?.(key)}
                    className={fieldClass}
                    placeholder={label}
                  />
                ) : (
                  <select
                    value={draft[key] || ''}
                    onChange={(e) => {
                      setField(key, e.target.value);
                      onFieldEdited?.(key);
                    }}
                    className={fieldClass}
                  >
                    <option value=""></option>
                    {options.map((opt) => {
                      if (opt && typeof opt === 'object' && 'value' in opt) {
                        return (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        );
                      }
                      return (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      );
                    })}
                  </select>
                )
              ) : inputKind === 'date' ? (
                <div>
                  <input
                    type="date"
                    min="1900-01-01"
                    max={maxBirthIso}
                    value={birthDateToHtmlInputValue(draft[key])}
                    onChange={(e) => { setField(key, e.target.value); onFieldEdited?.(key); }}
                    className={fieldClass}
                  />
                  {t('advisorPortal.dobInputHint') ? (
                    <p className="text-[10px] text-slate-400 mt-0.5 leading-snug">{t('advisorPortal.dobInputHint')}</p>
                  ) : null}
                </div>
              ) : inputKind === 'email' ? (
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={draft[key] || ''}
                  onChange={(e) => { setField(key, e.target.value); onFieldEdited?.(key); }}
                  className={fieldClass}
                />
              ) : inputKind === 'tel' ? (
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={draft[key] || ''}
                  onChange={(e) => { setField(key, e.target.value); onFieldEdited?.(key); }}
                  className={fieldClass}
                />
              ) : inputKind === 'passport' ? (
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  maxLength={20}
                  value={draft[key] || ''}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);
                    setField(key, v);
                    onFieldEdited?.(key);
                  }}
                  className={`${fieldClass} font-mono uppercase`}
                />
              ) : inputKind === 'nie' ? (
                <input
                  type="text"
                  inputMode="text"
                  maxLength={9}
                  placeholder={t('advisorPortal.nieInputPlaceholder')}
                  value={draft[key] || ''}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
                    setField(key, v);
                    onFieldEdited?.(key);
                  }}
                  className={`${fieldClass} font-mono uppercase`}
                />
              ) : inputKind === 'postal' ? (
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  value={draft[key] || ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setField(key, v);
                    onFieldEdited?.(key);
                  }}
                  className={`${fieldClass} font-mono`}
                />
              ) : (
                <input
                  value={draft[key] || ''}
                  onChange={(e) => { setField(key, e.target.value); onFieldEdited?.(key); }}
                  className={fieldClass}
                />
              )}
              {hasError ? <p className="text-[10px] text-rose-600 mt-1">{errors[key]}</p> : null}
            </div>
          );
        })}
      </div>
      <div className="flex flex-col gap-2 mt-3 min-[420px]:flex-row min-[420px]:items-center">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="min-h-[44px] text-xs font-semibold px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {saving ? t('advisorPortal.sectionSaving') : t('advisorPortal.sectionSave')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[44px] text-xs font-medium px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          {t('advisorPortal.sectionCancel')}
        </button>
      </div>
    </div>
  );
}
